import { SKU } from "@/entities/pricing/model/priceBook";
import { stripSystemPromptLines } from "@/shared/lib/customerPromptLines";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { acquireQuoteId, useQuotePreview } from "@/entities/pricing/model/useQuote";
import { autoUpgradeNotice } from "../model/videoTierPricing";
import { ArrowLeft, ChevronDown, Film, Info, Loader2, Save, Undo2, Wand2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import type { ExplosiveBreakdown, FormulaPayload } from "@/entities/explosive";
import { buildReferenceContent, buildPromptPreview } from "@/entities/explosive";
import { HandoffPreview, ReferenceContentPanel } from "@/entities/explosive";
import { toast } from "@/shared/hooks/use-toast";
import {
  briefPurposeByKey,
  briefPurposeOf,
  videoSceneKeyFor,
  type BriefPurposeDef,
} from "@/entities/scene-card/model/briefPurpose";
import {
  VIDEO_DEFAULT_CLIP_SECONDS,
  VIDEO_DEFAULT_RESOLUTION,
} from "@/entities/scene-card/model/creditRates";
import { clearSceneCardState, parseScenePreset } from "@/entities/scene-card/model/presets";
import type { PanelShotData } from "../model/panelShot";
import { useCompliance } from "@/contexts/AuthContext";
import {
  bareSendMaxSec,
  bareSendClampsTier,
  bareSendDurationSec,
  briefTargetSec,
  effectiveDurationSec,
} from "../model/effectiveDuration";
import { composeFailedNotice, frameFailedNotice } from "../model/composeFailNotice";
import { FREE_FLOW_INTAKE_HINT } from "../model/freeFlowHints";
import { effectiveTaskPurpose, intentProvenanceRef, purposeNotice } from "../model/purposeNotice";
import { emptyShot, formPatchFromShots, insertShotAt, shotsFromForm } from "../model/shotList";
import {
  predictVoiceoverCuts,
  voiceoverCutLine,
  voiceoverFixAdvice,
  type VoiceoverCutNotice,
} from "@/shared/lib/video/voiceoverCap";
import { VoiceoverDroppedList } from "@/shared/ui/VoiceoverCapHint";
import { videoProviderLabel } from "@/shared/lib/video/labels";
import { narrativeRoleLabel } from "../model/narrativeRoles";
import { hookVisualLabel } from "../model/hookVisual";
import { MATERIAL_DOC_HINT, looksLikeMaterialDoc } from "../model/briefCopyHint";
import {
  applyPurposeToForm,
  formulaIntakePlaceholder,
  formulaMaterialHint,
  isReplaceableBrief,
} from "../model/briefGuidance";
import {
  applyIntakeStash,
  clearIntakeStash,
  readIntakeStash,
  stashIntake,
} from "../model/videoIntakeStash";
import { pickResumableDraft } from "../model/resumeCompose";
import {
  applyStoryboardBackup,
  captureStoryboardBackup,
  hasStoryboardShots,
  type StoryboardBackup,
} from "../model/storyboardBackup";
import { resolveVideoSource } from "../model/videoSource";
import { videoStepOf } from "../model/videoStep";
import { BareSendConfirmDialog } from "../ui/components/BareSendConfirmDialog";
import { RecomposeConfirmDialog } from "../ui/components/RecomposeConfirmDialog";
import { VideoSourceBar, type VideoSourceBarData } from "../ui/components/VideoSourceBar";
import { VideoStepIndicator } from "../ui/components/VideoStepIndicator";
import { BlueprintStructureRail } from "../ui/components/BlueprintStructureRail";
import { VideoDirectorBriefCard } from "../ui/components/VideoDirectorBriefCard";
import {
  blueprintBriefPlaceholder,
  mapShotsToBeats,
  parseBeats,
} from "../model/blueprintStructure";
import { ProductImagePanel } from "../ui/components/ProductImagePanel";
import {
  applyCharacterPick,
  clearCharacterPick,
  composeResetAnchor,
  entitiesWithCharacter,
  mergeAnchorFromServer,
  realPersonBlockReason,
  stripLibCharacterEntities,
  withCharacterSheet,
  withIdentityReferences,
} from "../model/characterPick";
import { copyWithRealPersonBlockReason } from "../model/realPersonCopyGuard";
import { autoMotionByShot, isAutoMotion } from "../model/motionPick";
import {
  UPGRADE_BILLING_NOTE,
  upgradeNoticeSubject,
  willUpgradeSomeChunk,
} from "../model/upgradeNotice";
import { useReferenceThumbs } from "../model/useReferenceThumbs";
import { VideoAdvancedSection } from "../ui/components/VideoAdvancedSection";
import { VideoShootPlanCard } from "../ui/components/VideoShootPlanCard";
import { VideoDurationTierRow } from "../ui/components/VideoDurationTierRow";
import { VideoToneRegisterChips } from "../ui/components/VideoToneRegisterChips";
import { effectiveToneRegister } from "../model/toneRegister";
import { useCreativeTaxonomy } from "../model/useCreativeTaxonomy";
import { useTenantIndustryCode } from "../model/useTenantIndustryCode";
import { VideoClipDurationRow } from "../ui/components/VideoClipDurationRow";
import { VideoFormatRow } from "../ui/components/VideoFormatRow";
import { VideoRatioChips } from "../ui/components/VideoRatioChips";
import { FreeMaterialRow } from "../ui/components/FreeMaterialRow";
import { VideoPlanSummaryPanel } from "../ui/components/VideoPlanSummaryPanel";
import { VideoBgmPanel } from "../ui/components/VideoBgmPanel";
import { StoryboardToolbar } from "../ui/components/StoryboardToolbar";
import { VoiceSubtitlePanel } from "../ui/components/VoiceSubtitlePanel";
import { VideoCreateCockpit, VideoSubmitBar } from "../ui/components/VideoCreateCockpit";
import { VideoShotStrip } from "../ui/components/VideoShotStrip";
import { VideoTimelineBand } from "../ui/components/VideoTimelineBand";
import { VideoShotInspector } from "../ui/components/VideoShotInspector";
import { ShotLightbox } from "../ui/components/ShotLightbox";
import { VideoIntakeDeck } from "../ui/components/VideoIntakeDeck";
import { VideoPurposeBanner } from "../ui/components/VideoPurposeBanner";
import { VideoMaterialRow } from "../ui/components/VideoMaterialRow";
import { VideoIntakeSummaryBar } from "../ui/components/VideoIntakeSummaryBar";
import { OptionChip, OptionChipGroup } from "../ui/components/OptionChip";
import { composeInputDigest, isComposeStale } from "../model/composeDirty";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { Button } from "@/shared/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/ui/collapsible";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
import {
  videoService,
  type FromPosterBootstrapResponse,
  type VideoBgmDto,
  type VideoCapabilitiesResponse,
  type VideoDraftDto,
  type VideoTemplateDto,
  type TemplateFormField,
} from "../api/videoService";
import { VideoTemplateFillPanel } from "../ui/components/VideoTemplateFillPanel";
import { type FormState, withUserLocks } from "../model/videoCreateTypes";
import { alignPerShotArrays, mergeBootstrapRichFields } from "../model/mergeBootstrapRichFields";
import { buildTaskRequest } from "../model/buildTaskRequest";
import { useCompilePreview } from "../model/useCompilePreview";

// 模版 briefScript 里的 {key} 占位符替换成用户填的值,空值留原样标记(校验已挡 required 空)。
function fillBriefScript(script: string, values: Record<string, string>): string {
  return script.replace(/\{(\w+)\}/g, (_m, k: string) => (values[k] ?? "").trim() || `{${k}}`);
}
import { useVideoProviderConstraints } from "../model/useVideoProviderConstraints";
import { freeMaterialBlockReason } from "../model/freeMaterial";
import { composeEstimateCaption } from "../model/composeEstimateCaption";
import { posterBannerTitle } from "../model/posterBannerTitle";
import {
  composeMentionsFrames,
  composeScopeLabel,
  frameRuleCaption,
  framingBusyLabel,
  isManualTextOnly,
} from "../model/frameRuleCaption";
import { videoDraftName } from "../model/videoDraftName";
import { useComposeEstimate } from "../model/useComposeEstimate";
import { useVideoCreditEstimate } from "../model/useVideoCreditEstimate";
import { errorToast, errorText } from "@/shared/lib/apiErrors";

// FormState 已抽到 ../model/videoCreateTypes(供约束 / 积分估算 hook 共享)。
// 自由文案预填 brief 选项已随面板抽到 ../ui/components/VideoBriefPanel。

/**
 * V42 默认负面提示词 — 通用品质基线,任何题材都适用。
 * 来源:行业 SOP 通用经验(畸形/水印/手指变形/视频卡顿等是模型常见崩坏类别)。
 */
const DEFAULT_NEGATIVE_PROMPT =
  "畸形五官、歪脸、大小眼、肢体错位、模糊、低清晰度、水印、文字、多余肢体、手指变形、画面崩坏、卡顿、跳帧";

// 镜数以 panelScenes 为准(契约 v2 基准数组)。
function shotsCountOf(s: FormState): number {
  return s.panelScenes?.length ?? 0;
}

// inferMode / autoPickModel 已抽到 ../model/useVideoProviderConstraints。

export default function VideoCreatePage() {
  const navigate = useNavigate();
  // 1011 domesticOnly:真人出镜入口隐藏
  const { domesticOnly } = useCompliance();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const fromPoster = searchParams.get("fromPoster");
  /**
   * SEEDANCE 真人出镜 P0(0914 车 D2):真人出镜卡落地带 `?cast=real` —— 人物面板默认展开
   * (CharacterPanel 弹框自动开)、选择器默认过滤真人(realOnly);无真人人物的空态指路
   * `/characters/new-real`。只读 query,不写 state:不消耗 usage/scenePreset 那条锁定目的
   * 的通路,cast=real 是纯粹「带上真人出镜」这一件事,不代表选定了某个目的。
   */
  const castReal = searchParams.get("cast") === "real";
  /**
   * 1013 V6:视频舱「从产品库开始」带 `videoEntry:"product"` 落本页 —— 此前无消费方,
   * 用户落地后还得自己找素材行那颗「从产品库选」。现在进页即弹出产品库抽屉(同视觉舱
   * `posterEntry:"product"` 范式);一次性消费,刷新/回退不重放。
   * ★1014 复修:入口同时带 `?usage=goods`(产品带货目的流)—— 自由创作精简页没有产品库
   *   抽屉。标记做成 state:抽屉真弹出后回调 onProductLibraryAutoOpened 清掉 ——
   *   备料台之后再挂载(幕二「改设定」展开等)不重弹。
   */
  const [productEntry, setProductEntry] = useState(() => {
    const st = location.state as { videoEntry?: unknown } | null;
    const hit = !!st && typeof st === "object" && st.videoEntry === "product";
    if (hit) clearSceneCardState();
    return hit;
  });
  // 军师派引擎深链:location.state.prefillPrompt → 文案框(只填一次,对齐海报 FreeCreateBody 范式)。
  const prefilledRef = useRef(false);
  const assetSeq = searchParams.get("assetSeq");
  const templateId = searchParams.get("templateId");
  const [tpl, setTpl] = useState<VideoTemplateDto | null>(null);
  // 模版填空值(key→value);随模版 default 初始化。
  const [templateFieldValues, setTemplateFieldValues] = useState<Record<string, string>>({});

  const [caps, setCaps] = useState<VideoCapabilitiesResponse | null>(null);
  const [capsError, setCapsError] = useState<string | null>(null);
  const [bootstrapping, setBootstrapping] = useState(!!fromPoster);
  const [posterPrompt, setPosterPrompt] = useState<string | null>(null);
  const [posterTitle, setPosterTitle] = useState<string | null>(null);
  // 源海报的作品名(1006):装配响应没有内容标题时横幅退回它,见 posterBannerTitle 头注
  const [posterWorkName, setPosterWorkName] = useState<string | null>(null);
  const [posterPromptOpen, setPosterPromptOpen] = useState(false);
  // B2(2026-09-12):蓝图态「原片拆解」折叠(带给AI原文 + 参考片在拍什么合成一条),默认收起。
  const [originalCutOpen, setOriginalCutOpen] = useState(false);
  // 海报一键成片:用户意图(选填)+ 重新融合状态。意图独立于 userInput(后者是最终生成提示词)。
  const [posterIntent, setPosterIntent] = useState("");
  const [composing, setComposing] = useState(false);
  /**
   * **逐镜手动重出**的在途集合(用 Set 记镜号;原来单 number 会被后点的镜覆盖,看着像"取消"了前一个)。
   *
   * ★这条路确实可并行 —— 服务端 `regenerateOne` 原子写回,同时点几镜是安全的。
   * ★★但它**与批量出图不是一回事**:批量(compose-and-generate)在服务端是
   *   `VideoDraftOrchestrationService.generateFrames` 的 for 循环,**按镜序串行**,
   *   因为后一镜要锚定前一镜已出的图才能保跨镜同人(anchorFor 的串行依赖)。
   *   把这两条混为一谈,正是界面上那句「并行出首帧中…」的来历 —— 2026-08-06 已改口。
   *   以后改这里的文案,先分清说的是哪一条路。
   */
  const [framingSet, setFramingSet] = useState<ReadonlySet<number>>(() => new Set());
  const [framingBatch, setFramingBatch] = useState(false);
  // 2026-08-04 导演自动配动作:编排回执快照(shotIdx→assetRef)。「AI 配」徽标 + 概览
  // 「动作×N(AI 配)」的判据 —— 用户换/清动作后 isAutoMotion 判据自然失效,重编排时重置。
  // 会话级标注:草稿重开不恢复(chips/蓝条仍照 form.references 如实显示,不比实际乐观)。
  const [autoMotion, setAutoMotion] = useState<Record<number, string>>({});

  const [form, setForm] = useState<FormState>({
    userInput: "",
    name: "",
    useAutoModel: true,
    provider: "seedance",
    model: "doubao-seedance-2-0-fast-260128",
    generationMode: "T2V",
    // A6(2026-08-01):默认档引用 creditRates 的单一真源 —— 入口卡面报的就是这两个值。
    // 此前这里是字面量 "720p"/5,而卡面另写死「15 秒」,两边各改各的 → 卡面 8280、落地 3280。
    resolution: VIDEO_DEFAULT_RESOLUTION,
    ratio: "9:16",
    durationSec: VIDEO_DEFAULT_CLIP_SECONDS,
    generateAudio: false,
    source: "FREE",
    bgmId: null,
    customBgmObjectKey: null,
    negativePrompt: DEFAULT_NEGATIVE_PROMPT,
    // 故事引擎:默认【关】—— 多数 brief 是公司/多产品介绍,非故事模式覆盖更全、动效更足(全 T2V)。
    // 故事模式是「聚焦单个产品/一个重点、讲有主角的小故事」时用户主动开(开时自动锁主角)。
    storyMode: false,
    useProtagonistAnchor: false,
    storyBible: null,
    // ratio 默认即锁:比例是用户上来就见到的格式选择(竖/横/方,无"智能"态),可见的默认 9:16 就是
    // 用户的选择,AI 编排不该把它改掉。配音/字幕等"先 AI 建议再改"的字段不默认锁。
    lockedFields: ["ratio"],
  });
  /**
   * 幕二里备料台是否展开(D4:编排后仍允许改设定,但要重编排才生效)。
   * 幕一恒展开 —— 那一幕它就是主体。
   */
  const [deckOpen, setDeckOpen] = useState(false);
  /**
   * 「当前这组分镜是按哪份输入编排的」指纹。**在分镜产出的那一刻取**,不是提交那一刻:
   * `triggerComposeJob` 会把 userInput 换成追加了人物卡的版本(`withCharacterSheet`),
   * 提交时取指纹的话一编排完就恒 stale(误报)。
   * 判据本身在 `composeDirty.ts`(钉 ai 侧 ComposeRequest 字段集)。
   */
  const [composeSnapshot, setComposeSnapshot] = useState<string | null>(null);

  // 军师派引擎深链预填:把 brief 写进 userInput(只填一次,且不覆盖用户已输入)。
  useEffect(() => {
    const pf = (location.state as { prefillPrompt?: string } | null)?.prefillPrompt;
    if (pf && !prefilledRef.current && !form.userInput) {
      prefilledRef.current = true;
      setForm((s) => ({ ...s, userInput: pf }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // 爆款蓝图预载(P-A 视频闭环 + M2 蓝图条):从 /ai-explosive 直跳,蓝图 payload 落 form
  // (随 formJson 进草稿),backend brief() 把 formulaPayload+fidelityMode 透传 ai compose;
  // 编排成功后 backend 登记蓝图取用(usedDests.video+1)。复刻档默认「学套路」。
  //
  // ★2026-08-12:**不再往 userInput 里写结构底稿**。原来那句 290+ 字的「结构:…」
  //   会被 ai 判成 SELLING_POINTS,于是引擎把节奏描述当卖点逐条落进口播
  //   (用户报的「带过去的都是节奏」)。结构走 formulaPayload 通道进渲染链,
  //   界面上由蓝图条 + 结构轨呈现;文案框留给用户写自己的产品(placeholder 引导)。
  const explosivePrefilledRef = useRef(false);
  /** 路由带来的「参考片在拍什么」原文 —— 用户改乱了能一键还原(纯展示态,不入 form)。 */
  const referenceContentOriginalRef = useRef<string | null>(null);
  useEffect(() => {
    const h = (
      location.state as {
        explosive?: {
          blueprintId: number;
          formulaName: string;
          payload: Record<string, unknown>;
          sourcePlatform?: string;
          /** 2026-08-14:拆解报告(「参考片在拍什么」的数据源) */
          breakdown?: ExplosiveBreakdown | null;
          /** EXP-RAW-1(2026-09-02):蓝图留了原片 → 仿拍档可参考原片画面(r2v) */
          hasRawVideo?: boolean;
          /** BPWB-1(2026-09-03):路由侧定的默认档(有原片+场景层 → "copy")。 */
          fidelityMode?: string;
        };
      } | null
    )?.explosive;
    // 轻 shape 校验:location.state 可被伪造(同军师深链信任级),逐字段验型再用。
    const valid =
      h != null &&
      typeof h.formulaName === "string" &&
      typeof h.blueprintId === "number" &&
      h.payload != null &&
      typeof h.payload === "object";
    if (valid && !explosivePrefilledRef.current) {
      explosivePrefilledRef.current = true;
      const refContent = buildReferenceContent(h.breakdown as ExplosiveBreakdown | null) || null;
      referenceContentOriginalRef.current = refContent;
      setForm((s) => ({
        ...s,
        explosiveBlueprintId: h.blueprintId,
        explosiveFormulaPayload: JSON.stringify(h.payload),
        explosiveFormulaName: h.formulaName,
        // BPWB-1(2026-09-03 拍板):默认档由路由侧按「有原片+场景层」定成仿拍;
        // 草稿里已有用户选过的档优先,路由没带(存量入口/伪造 state)仍落学套路。
        explosiveFidelityMode:
          s.explosiveFidelityMode ?? (h.fidelityMode === "copy" ? "copy" : "learn"),
        // 仿拍档比例跟原片锁死(拍板项③:爆款原片基本都是竖屏;格式行在仿拍档不渲染,
        // 不锁的话 form 里躺着的旧值会静默上送)。
        ...(s.explosiveFidelityMode == null && h.fidelityMode === "copy" ? { ratio: "9:16" } : {}),
        explosiveSourcePlatform:
          typeof h.sourcePlatform === "string" ? h.sourcePlatform : s.explosiveSourcePlatform,
        // EXP-RAW-1:同 sourcePlatform 一路验型 —— 只认真布尔,别的(含缺省)一律按「没原片」,
        // 说大话的代价是用户以为在照原片仿拍、成片却是纯想象。
        explosiveHasRawVideo: h.hasRawVideo === true,
        // 2026-08-14「参考片在拍什么」:装成人读的文本随草稿走(重开草稿不丢)。
        // ★独立字段,**不进 userInput** —— 进了会被 classify_input 判成用户卖点,
        //   成片就讲参考片的产品(0812 实证)。用户的产品事实仍然只在「想拍什么」框里。
        explosiveReferenceContent: refContent,
      }));
    }
  }, [location.state]);

  /**
   * A1 目的锁定(2026-09-12)—— **目的的唯一真源改成 URL `?usage=<key>`**。
   *
   * 优先级:`?usage=` > `location.state.scenePreset`。
   *   - `?usage=` 保住刷新 / 直链 / 分享(state 一刷新就没了,query 还在);
   *   - `scenePreset` 只做**首跳加速**(hub 点卡时 state 一到就先种,不必等 URL 解析)——
   *     一次性消费 + `clearSceneCardState`(刷新不重放),这套逻辑照旧保留。
   * fromPoster / 模板 / 蓝图 / 军师深链等入口没有 usage、目的另有来路 → 早退,行为不变。
   *
   * ★A3「已填内容会保留」:回 hub 前把想拍什么 + 素材引用暂存进 sessionStorage;
   *   在这里(选任一目的进来时)若暂存在场就叠回来(种完 briefSeed 之后,暂存的字优先)。
   * ★storyMode 映射统一走 `applyPurposeToForm`(目的的唯一写入口)——「剧情微故事」自动开
   *   故事引擎 + 锁主角,与页内选目的一致(旧 scenePreset 分支手写 setForm 漏了这层映射)。
   */
  const scenePresetRef = useRef(false);
  // B6 场景打标:preset 消费时记下,提交随请求上送(直接进页=null 不打标)。
  const sceneKeyRef = useRef<string | null>(null);
  // 来源条要显示的目的(渲染态,不能只留在 ref 里 —— ref 不触发重渲)。
  const [purposeSource, setPurposeSource] = useState<BriefPurposeDef | null>(null);
  useEffect(() => {
    if (scenePresetRef.current || explosivePrefilledRef.current) return;
    if (fromPoster || templateId) return; // 这些入口的目的另有来路,不吃 usage/目的卡
    const preset = parseScenePreset(location.state);
    const presetDef = preset && preset.kind === "video" ? briefPurposeOf(preset.purpose) : null;
    const usageDef = briefPurposeByKey(searchParams.get("usage"));
    const def = usageDef ?? presetDef; // ?usage 优先;都没有就没锁定目的(自由态)
    if (!def) return;
    scenePresetRef.current = true;
    if (preset && preset.kind === "video") clearSceneCardState(); // 一次性消费 state
    sceneKeyRef.current = `video_${def.key}`; // B6 打标(来自目的卡 / usage 直链)
    setPurposeSource(def);
    // A3:回 hub 暂存过就叠回(想拍什么 + 素材);否则按目的种 briefSeed。
    const stash = readIntakeStash();
    setForm((s) => {
      const applied = applyPurposeToForm(s, def.label);
      return stash ? applyIntakeStash(applied, stash) : applied;
    });
    if (stash) clearIntakeStash();
    // scenePreset-only 入口(存量卡片没带 usage)→ 补写 usage,刷新才保得住目的。
    if (searchParams.get("usage") !== def.key) {
      const sp = new URLSearchParams(searchParams);
      sp.set("usage", def.key);
      setSearchParams(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  /**
   * 波2:**换了目的,已选拍法作废**。
   *
   * 拍法卡是按目的分组的(`cards_for_purpose`):在「产品带货」下选了「开箱首体验」,再把目的
   * chip 改成「招聘」,那张卡对新目的根本不成立。引擎侧已有第二道门(`user_pick` 校验卡属不属于
   * 当前目的,不属于就退回自动),但**光靠引擎兜底不够** —— 面板上那行还写着「已选开箱首体验」,
   * 而成片按招聘档自动选的拍法拍:又一次「显示与实际两个出口说两套话」。这里在改目的的当下就清掉。
   *
   * ★`prev != null` 是必要的守卫,不是防御性冗余:草稿恢复 / 目的卡预填都是「从无到有」地写入
   * briefPurpose,若不守就会在恢复草稿的那一帧把用户存着的拍法一起抹掉。
   */
  const lastPurposeRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const prev = lastPurposeRef.current;
    const cur = form.briefPurpose;
    lastPurposeRef.current = cur;
    if (prev != null && prev !== cur && form.pickedFormulaKey) {
      // A7②:换目的 → 已选拍法对新目的不成立,清掉并**显式提示**(此前静默,用户以为还在用旧拍法)。
      // 读 form.pickedFormulaKey(本轮渲染值,清空发生在下面 setForm 之后)判断是否真有拍法被清。
      toast({
        title: "已取消之前选的拍法",
        description: "拍法是按目的分的,换了目的就不成立了 —— 这条片改由引擎按新目的重新选拍法。",
      });
      setForm((s) =>
        s.pickedFormulaKey ? { ...s, pickedFormulaKey: null, pickedFormulaName: null } : s
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 只在目的变化时跑;pickedFormulaKey 读当轮值(清空前),不入依赖
  }, [form.briefPurpose]);

  /**
   * 调性档默认值(0924 车 C):用户没点过 chip → 按租户行业写入生效档(与 ai 同表,见 toneRegister.ts);
   * 点过(toneRegisterTouched)就不再跟行业。form 里始终存「生效档」,编排 formJson / 建任务 / 拍法卡
   * taxonomy 都直接读它,不各自再推一遍。草稿回填、整表替换后若丢了这个字段,这里会补回。
   */
  const tenantIndustryCode = useTenantIndustryCode();
  const toneRegisterNow = effectiveToneRegister(form, form.industryCode ?? tenantIndustryCode);
  useEffect(() => {
    if (form.toneRegister === toneRegisterNow) return;
    setForm((f) => {
      const next = effectiveToneRegister(f, f.industryCode ?? tenantIndustryCode);
      return f.toneRegister === next ? f : { ...f, toneRegister: next };
    });
  }, [form.toneRegister, toneRegisterNow, tenantIndustryCode]);

  // 拍法备料 hint(0824):选中的拍法卡节拍里带 {槽位} 原文 —— 这张卡要用到什么料,
  // 槽位就是清单本身。taxonomy 与 VideoFormulaPicker 同 queryKey/staleTime,缓存命中零多拉;
  // 只有真选了拍法才需要(enabled 门:没选卡的用户不为一行 hint 白拉一次)。
  const taxonomyQuery = useCreativeTaxonomy(form.toneRegister, {
    // D 批「想拍什么×时长联动」也要读它(内容位数);选了目的即取,与拍法选择器 / 时长卡同缓存零多拉。
    enabled: !!form.pickedFormulaKey || !!briefPurposeOf(form.briefPurpose),
  });
  /**
   * A7①(2026-09-13):粘了「现成分镜脚本」时引擎会忠实拆解、**不套用**用户选的拍法 —— 拍法被静默丢弃。
   * 但「是不是脚本」的判据(`looks_like_script`)在**服务端**,前端不复刻那套正则(复刻 = 第二份判据必漂移)。
   * 所以本批不做主动 toast,只保留 VideoFormulaPicker 卡墙里那句被动提示;
   * 「粘了脚本 → 提示所选拍法失效」这一条**留给 B 批**:等 compose 响应回 input_kind=SCRIPT 信号再提示。
   */
  const pickedCard = useMemo(() => {
    if (!form.pickedFormulaKey) return null;
    return (
      taxonomyQuery.data?.purposes
        ?.flatMap((p) => p.formulas)
        .find((c) => c.key === form.pickedFormulaKey) ?? null
    );
  }, [taxonomyQuery.data, form.pickedFormulaKey]);
  const formulaHint = pickedCard ? formulaMaterialHint(pickedCard) : null;
  // A3:选了拍法 → 「想拍什么」空态 placeholder 用该拍法节拍的 {槽位} 组的提问骨架(纯结构卡返 null)。
  const formulaPlaceholder = pickedCard ? formulaIntakePlaceholder(pickedCard) : null;
  /**
   * D 批「想拍什么×时长联动」:当前目的×当前时长档的内容位数(taxonomy 的 beatPlan.contentBeats)——
   * 想拍什么标题旁「这个时长讲 N 个点·建议写 N 个卖点」用它,改时长档 N 随之变。数据源与
   * DurationAdviceLine / durationFit 同一份(不另立经验值)。
   */
  const intakeContentBeats = useMemo(() => {
    const sec = briefTargetSec(form.briefDuration);
    if (!sec) return undefined;
    return taxonomyQuery.data?.purposes
      ?.find((p) => p.purpose === form.briefPurpose)
      ?.beatPlans?.find((b) => b.targetSec === sec)?.contentBeats;
  }, [taxonomyQuery.data, form.briefPurpose, form.briefDuration]);

  // 蓝图条数据:全部从 form 派生(交接与草稿重开同源,无第二份状态)。
  const blueprint = useMemo(() => {
    if (!form.explosiveFormulaName || !form.explosiveFormulaPayload) return null;
    try {
      const p = JSON.parse(form.explosiveFormulaPayload) as Record<string, unknown>;
      const sceneCards = Array.isArray(p.sceneCards) ? p.sceneCards : [];
      return {
        name: form.explosiveFormulaName,
        beats: parseBeats(typeof p.beats15Raw === "string" ? p.beats15Raw : ""),
        hasSceneCards: sceneCards.length > 0,
        copyFormula: typeof p.copyFormula === "string" ? p.copyFormula : "",
        // 整包留着给「这次带给 AI 的原文」面板 —— 它要的正是随 formulaPayload 上送的这份
        payload: p as FormulaPayload,
      };
    } catch {
      // 草稿里的 payload 串损坏:不出蓝图条,编排时后端仍会按无蓝图处理
      return null;
    }
  }, [form.explosiveFormulaName, form.explosiveFormulaPayload]);

  // 结构轨双向 hover(结构轨节拍 ↔ 分镜卡挂拍标签)。
  const [hoveredBeat, setHoveredBeat] = useState<number | null>(null);
  const fidelityMode = form.explosiveFidelityMode === "copy" ? "copy" : "learn";
  /**
   * 0924 车 D:仿拍(copy)× 真人人物 → 当场拦(D-ai `COPY_WITH_REAL_PERSON_UNSUPPORTED` 同源人话)。
   * 此前这条路前端畅通、ai 静默丢身份照扣钱(片里没有本人)。编排按钮 / 直接出片 / onSubmit /
   * handleCompose 四处同读这一个值。
   */
  const copyRealBlock = copyWithRealPersonBlockReason({
    hasBlueprint: !!blueprint,
    fidelityMode,
    characterIsRealPerson: form.characterIsRealPerson,
  });
  const clearBlueprint = () =>
    setForm((s) => ({
      ...s,
      explosiveBlueprintId: null,
      explosiveFormulaPayload: null,
      explosiveFormulaName: null,
      explosiveFidelityMode: null,
      explosiveSourcePlatform: null,
      explosiveHasRawVideo: null,
      panelBeats: null,
      // ★2026-08-12:userInput 不再需要跟着清 —— 蓝图不再往里写任何东西,
      //   框里的字从头到尾都是用户自己敲的,清蓝图不该动用户的文案。
    }));

  /**
   * 波1 来源收敛:五来源的显隐**一处判定**(`resolveVideoSource`),页面各处正面读字段。
   * 收敛前是一层层手写否定式 if(`!fromPoster && !templateId && !(panelScenes>=1)`),
   * 每加一个来源都要去补每一个 if,漏一处的表现是两个面板同时出现 —— 只有真跑那条来源才看见。
   * 真值表(五来源 × 出分镜前后 10 格)在 `model/videoSource.test.ts` 逐格锁死。
   */
  const srcUi = resolveVideoSource({
    fromPoster: !!fromPoster,
    templateId: !!templateId,
    hasBlueprint: !!blueprint,
    purpose: purposeSource,
    hasStoryboard: !!form.panelScenes && form.panelScenes.length >= 1,
    // BPWB-1:仿拍档格式行不出(时长由原片定),判定收在 videoSource 单点
    fidelityMode: blueprint ? fidelityMode : null,
  });

  /**
   * B6 场景打标(随提交上送)—— ★跟着**当前**目的走,不是进页时那张卡。
   *
   * 目的 chip 在下面的 brief 面板里可改:从「招聘」卡进来再换成「公司介绍」,提交的 `purpose`
   * 是公司介绍,若 `sceneKey` 还是进页时记下的 `video_recruit`,这条片就被塞进**错误的场景桶**
   * —— 场景筛按 sceneKey 分组筛不到它,列表上「来自哪张卡」显示的也是假的
   * (codex review 第三轮 P2)。
   *
   * 只有**从目的卡来**的才打标(`purposeSource` 非空):sceneKey 的语义是「来自哪张卡」,
   * 自由流的人自己在 chip 上选了个目的,并不等于他从那张卡来 —— 那种情况保持不打标。
   * 当前目的不在 8 值里(草稿带来的下线字面量)→ 退回进页时那张卡的标,不打假标。
   */
  const sceneKeyForSubmit = purposeSource
    ? (videoSceneKeyFor(form.briefPurpose) ?? sceneKeyRef.current)
    : sceneKeyRef.current;

  /**
   * 目的的唯一写入口(两态收敛批 D1/D2,2026-07-26)—— 自由流备料台①选/清目的。
   *
   * ★storyMode 映射 + 推荐输入 seed 置换(0824)收进 applyPurposeToForm 单点:
   * 页内选目的时,文案框里没被用户动过的模板跟着换档;用户写过的字一个不动。
   * 已选拍法的作废由下面监听 briefPurpose 的 useEffect 处理(波2 定案),这里不重复写。
   *
   * ★A1 目的锁定(2026-09-12):选定的目的**同步写进 URL `?usage=`** —— 目的的真源是 URL,
   *   页内选一次就锁定(横幅接管、目的行退场,刷新也保得住);选「AI 判断」(null)清掉 usage。
   */
  const applyPurpose = (label: string | null) => {
    setForm((s) => applyPurposeToForm(s, label));
    const key = label ? briefPurposeOf(label)?.key : null;
    const sp = new URLSearchParams(searchParams);
    if (key) sp.set("usage", key);
    else sp.delete("usage");
    setSearchParams(sp, { replace: true });
  };

  /** A3「已填内容会保留」:回视频舱前暂存想拍什么 + 素材引用,再选任一目的进来时叠回。 */
  const backToHub = () => {
    stashIntake(form);
    navigate("/ai-video");
  };

  /** 来源条数据:按已判定的 kind 取对应那一份(同一个挂载点,见 VideoSourceBar)。 */
  const sourceBarData: VideoSourceBarData =
    srcUi.kind === "explosive" && blueprint
      ? {
          kind: "explosive",
          formulaName: blueprint.name,
          sourcePlatform: form.explosiveSourcePlatform,
          fidelityMode,
          hasSceneCards: blueprint.hasSceneCards,
          // EXP-RAW-1:仿拍档说明文案分叉的唯一依据(存量草稿无此字段 → false)
          hasRawVideo: form.explosiveHasRawVideo === true,
          // BPWB-1:工作卡播原片现签直链;幕二收紧凑态(播放位/结构轨让位给导演台)。
          // ★hasStoryboard 变量声明在下文(TDZ),这里用与 srcUi 同一句判据。
          blueprintId: form.explosiveBlueprintId,
          compact: !!form.panelScenes && form.panelScenes.length >= 1,
          payload: blueprint.payload,
          // 切到仿拍时比例跟原片锁死(9:16);切回学套路留原值,格式行那时会重新出现
          onFidelityChange: (m) =>
            setForm((s) => ({
              ...s,
              explosiveFidelityMode: m,
              ...(m === "copy" ? { ratio: "9:16" } : {}),
            })),
          onSwap: () => navigate("/ai-explosive"),
          onClear: clearBlueprint,
        }
      : srcUi.kind === "template"
        ? {
            kind: "template",
            // 模板详情异步到手前先用占位名,来源条不闪空(判据看 templateId,同 srcUi)
            name: tpl?.name ?? "模板加载中…",
            description: tpl?.description,
            coverUrl: tpl?.coverUrl,
            onChange: () => navigate("/ai-video/templates"),
          }
        : srcUi.kind === "poster"
          ? {
              kind: "poster",
              title: posterBannerTitle(posterTitle, posterWorkName),
              bootstrapping,
              // 1006:文案写「见上方缩略图」,横幅却只有图标 —— 参考帧就是这张海报
              thumbUrl: form.firstFrameUrl,
            }
          : (briefPurposeOf(form.briefPurpose) ?? (srcUi.kind === "purpose" ? purposeSource : null))
            ? {
                kind: "purpose",
                /**
                 * ★显示**当前**目的(codex review 第二轮 P2 的口径升级):
                 * 两态收敛批起,目的的唯一出口 = 来源条 —— 不论从目的卡进来(purposeSource)
                 * 还是自由态在 popover 里选的,只要 `form.briefPurpose` 有效就按 purpose 形态
                 * 显示。此前自由态选了目的后来源条仍写「自己写,不套目的」= 来源条自己撒谎。
                 * `purposeSource` 继续只管一件事:sceneKey 打标(「来自哪张卡」,页内选的不打标)。
                 * 草稿带已下线字面量 → briefPurposeOf null → 从卡来的退回那张卡,否则回 free。
                 */
                purpose: (briefPurposeOf(form.briefPurpose) ?? purposeSource)!,
                // 波2:选了拍法就把那句「拍法由 AI 自动选」换成实际选的那张(同上一条理由)
                formulaName: form.pickedFormulaName,
                // Q3:剧情微故事自动开故事引擎,来源条明示(自动行为要可见)
                storyEngine:
                  (briefPurposeOf(form.briefPurpose) ?? purposeSource)?.key === "narrative",
                /**
                 * ★VID-CMP-06:手选档与 free 档吃**同一份** purposeNotice(form)。
                 * ai/api 早把 purposeHint/purposeHintReason 送进了 formJson,措辞层
                 * (purposeNotice user 分支)也早产出「按「X」编排」+ 分歧留痕 ——
                 * 断的只是这条接线:purpose 分支此前根本不调它,AI 的反对意见全程静默。
                 */
                notice: purposeNotice(form),
              }
            : {
                kind: "free",
                /**
                 * ★2026-08-02:改吃 ai 回传的**出处**,不再本地算兜底档。
                 *
                 * 原来这里是 `resolveEffectivePurpose(form.briefPurpose).source === "default"`
                 * → 显示「未选目的 → AI 按『产品带货』编排」。当时对(引擎确实不看文案);
                 * ai 侧 `intent_infer` 之后引擎真会判成别的档,再写死兜底档就是**说少了**。
                 *
                 * console 只显示不推断 —— `effectivePurpose.ts` 自己的告诫:「console 本地
                 * 关键词推断 → 与引擎两份判据,必漂移」。编排前 notice 是 pending 态,
                 * 只说「AI 会按你的文案判」,不预言判成什么。
                 */
                notice: purposeNotice(form),
              };

  // BGM 状态
  const [bgms, setBgms] = useState<VideoBgmDto[]>([]);
  const [bgmTab, setBgmTab] = useState<"preset" | "custom">("preset");
  const [customBgmFile, setCustomBgmFile] = useState<File | null>(null);
  const [customBgmUrl, setCustomBgmUrl] = useState<string | null>(null);
  const [uploadingBgm, setUploadingBgm] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  /**
   * 出片 / 编排的**在途闸**(0811 爱民「不知道是网速慢还是服务器问题,点了 3 次 直接出片才开始制作」)。
   *
   * ★为什么是 ref 而不是上面那两个 state:`submitting` / `composing` 在**同一轮事件里读到的还是旧值**
   *   —— 两次点击落在同一 tick 时两次都看见 false,于是建两单、扣两份钱。按钮的 `disabled` 也救不了:
   *   它同样要等下一次渲染才生效。确认弹窗那颗「仍直接出片」尤其危险 —— 它是点完之后才靠
   *   setState 把自己卸载掉的,双击的第二下正好落在这个空档里。
   * ★ref 同步写、同步读,且写在**第一个 await 之前**(前面全是同步校验),所以第二下必然看见 true。
   * ★两条路共用一个闸:编排与直发本来就互斥(彼此的按钮都会因对方在途而 disabled),
   *   而它们花的是同一份积分。
   */
  const inFlightRef = useRef(false);
  /**
   * 「不编排直接出片」的确认(2026-08-06 重排后**改造保留**)。
   *
   * 重排前它是「没有分镜就点了右栏生成视频」的劝返弹窗 —— 那是**用弹窗补布局的错**:
   * 两颗同样亮的实心按钮同屏竞争,点错是版面造成的。幕一去掉右栏后那个误点场景消失了。
   * 但这条路本身有人要用(粘好的单镜描述、快速试片),所以按次级文字按钮补回,
   * 弹窗的职责随之改变:不再是拦误点,而是**给明知故犯者一次知情确认**(要花钱 + 效果通常差)。
   */
  const [bareSendConfirmOpen, setBareSendConfirmOpen] = useState(false);
  /**
   * 「重新编排」知情确认(批⑫a 重排安全网)。**只在已有分镜时**开 —— 判据与弹窗理由
   * 见 components/RecomposeConfirmDialog 头注,闸在 `guardedCompose`。
   * 待执行的那一次编排存 ref(不进 state:它只在用户点确认时被读一次,不参与渲染)。
   */
  const [recomposeConfirmOpen, setRecomposeConfirmOpen] = useState(false);
  const pendingComposeRef = useRef<(() => Promise<void>) | null>(null);
  /**
   * 上一版分镜(批⑫a 撤销底座)。**单槽**:新的覆盖旧的,点「恢复上一版分镜」时与当前版互换,
   * 所以能来回切。会话内 state,不落 localStorage —— 跨标签/跨设备的一致性不值得为一个撤销键背,
   * 而这条路上真正要救的是「刚刚点错」的那三十秒。字段集与理由见 model/storyboardBackup.ts。
   */
  const [storyboardBackup, setStoryboardBackup] = useState<StoryboardBackup | null>(null);
  // 尾句裁切提交明示(0817 拍板):提交前算出 ai #18 硬裁会落在哪,知情同意后照裁。
  const [voCutNotices, setVoCutNotices] = useState<VoiceoverCutNotice[] | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // 草稿:编排出 storyboard(真出图扣费)后自动存 + 手动存,继续编辑恢复 form
  const [currentDraftId, setCurrentDraftId] = useState<number | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  // 服务端出图 job 状态(轮询 getDraft 拿):IDLE/RUNNING/DONE/PARTIAL/FAILED。
  const [frameGenStatus, setFrameGenStatus] = useState<string>("IDLE");
  /**
   * COMPOSE_FAILED 的钱口径(VID-REFUND-1)。空 = 后端没给(旧数据)→ 按"不确定"措辞,
   * **不许**当成"没扣费"(缺席≠没扣,这正是这次要修的那类假话)。
   */
  const [composeFailKind, setComposeFailKind] = useState<string | null>(null);
  const draftRestoredRef = useRef(false);
  // 草稿 id 最新快照(轮询 / 重渲要用)+ 轮询 timer。
  const draftIdRef = useRef<number | null>(currentDraftId);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 轮询时是否已把服务端编排出的 storyboard 灌回 form(只灌一次,之后只更新帧不冲掉编辑)。
  const storyboardHydratedRef = useRef(false);
  useEffect(() => {
    draftIdRef.current = currentDraftId;
  }, [currentDraftId]);
  // 卸载时停轮询(出图 job 在服务端继续,不受影响;回来 ?draftId 恢复时再续轮询)。
  useEffect(() => {
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, []);

  // M2 蓝图编排完成 toast 上下文(triggerComposeJob 设置,首次 hydrate 消费)。
  const blueprintComposeRef = useRef<{ beatsCount: number } | null>(null);

  // 出图进行中守卫:出图在服务端跑、切走刷新不丢帧;但关标签/刷新会停掉本页轮询,弹原生确认提示一下。
  useEffect(() => {
    if (!framingBatch) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [framingBatch]);

  // 1. 加载 capabilities + BGM
  useEffect(() => {
    let cancelled = false;
    videoService
      .getCapabilities()
      .then((c) => !cancelled && setCaps(c))
      .catch((e) => !cancelled && setCapsError(errorText(e)));
    videoService
      .listBgms()
      .then((list) => !cancelled && setBgms(list))
      .catch(() => {
        // BGM 拉不到不阻塞,只是没预置可选
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 1.5 BGM 列表 + LLM 推荐 mood 都到位时,自动选匹配 mood 的第一首 BGM(让视频默认有声不静音)。
  // 用户手动改 BGM 选择后(form.bgmId !== null),不再覆盖,尊重用户选择。
  useEffect(() => {
    if (
      !form.suggestedBgmMood ||
      bgms.length === 0 ||
      form.bgmId != null ||
      form.customBgmObjectKey
    )
      return;
    const matched = bgms.find((b) => b.enabled && (b.tags || []).includes(form.suggestedBgmMood!));
    if (matched) {
      setForm((s) => ({ ...s, bgmId: matched.id }));
    }
  }, [form.suggestedBgmMood, bgms, form.bgmId, form.customBgmObjectKey]);

  // 2026-08-04 导演自动配动作:记编排回执快照 + 主动告知(诚实三原则:看得见/改得掉/算得清)。
  const recordAutoMotion = (refs: FormState["references"]) => {
    const auto = autoMotionByShot(refs ?? undefined);
    setAutoMotion(auto);
    const n = Object.keys(auto).length;
    if (n > 0) {
      toast({
        title: `AI 已为 ${n} 镜自动配了动作模板`,
        description:
          "逐镜卡「动作」处可换可清;含动作参考的段落将升级「综合」档出片,按该档计费(详见下方蓝条)。",
      });
    }
  };

  // 海报 bootstrap 响应 → 装配 form(初次加载 + 「融合海报+意图重新生成」复用同一套填充逻辑)
  // 返回算好的 next form,供调用方存草稿 + 触发服务端出图(避开 setForm 后 formRef 仍是旧值的坑)。
  const applyBootstrap = (b: FromPosterBootstrapResponse): FormState => {
    setPosterTitle(b.title ?? null);
    setPosterPrompt(b.promptHint ?? null);
    // 用户手选优先:locked 字段保留 form 值,不被 AI suggestion 覆盖。
    const locked = new Set(form.lockedFields ?? []);
    const next: FormState = {
      ...form,
      source: "FROM_POSTER",
      sourceRef: {
        fromPosterTaskId: Number(fromPoster),
        fromPosterAssetSeq: assetSeq ? Number(assetSeq) : undefined,
      },
      firstFrameUrl: b.referenceImageUrl,
      styleCode: b.styleCode,
      ratio: locked.has("ratio") ? form.ratio : b.suggestedRatio || form.ratio || "9:16",
      // durationSec 是「每镜/单镜」时长(请求级 ≤15);多格的总时长在 panelDurations。
      // 多格时取首镜时长,别把 suggestedDuration(可能是各镜之和)塞进来。
      durationSec: Math.min(
        15,
        Math.max(2, (b.panelDurations && b.panelDurations[0]) || b.suggestedDuration || 5)
      ),
      name: b.title ? `${b.title} 视频版` : form.name,
      // V42.x:吸收 LLM 一次性产出的所有结构化字段(单图也走)
      // 契约 v2:panelScenes(环境)是基准数组,panelMotions(动作)是与它并列的第二个窄口 ——
      // 漏合并 motions = 导演产的动作整列丢失,而 `?? form.x` 不会报任何错。
      panelScenes: b.panelScenes && b.panelScenes.length > 0 ? b.panelScenes : form.panelScenes,
      panelMotions:
        b.panelMotions && b.panelMotions.length > 0 ? b.panelMotions : form.panelMotions,
      panelSubjectRefs:
        b.panelSubjectRefs && b.panelSubjectRefs.length > 0
          ? b.panelSubjectRefs
          : form.panelSubjectRefs,
      anchorKey: b.anchorKey ?? form.anchorKey,
      panelDurations:
        b.panelDurations && b.panelDurations.length > 0 ? b.panelDurations : form.panelDurations,
      negativePrompts:
        b.negativePrompts && b.negativePrompts.length > 0
          ? b.negativePrompts
          : form.negativePrompts,
      voiceovers: b.voiceovers && b.voiceovers.length > 0 ? b.voiceovers : form.voiceovers,
      voiceTone: locked.has("voiceTone")
        ? form.voiceTone
        : (b.suggestedVoiceTone ?? form.voiceTone),
      voiceEmotion: locked.has("voiceEmotion")
        ? form.voiceEmotion
        : (b.voiceEmotion ?? form.voiceEmotion),
      voiceEmotions:
        b.voiceEmotions && b.voiceEmotions.length > 0 ? b.voiceEmotions : form.voiceEmotions,
      theme: b.theme ?? form.theme,
      brandFocus: b.brandFocus ?? form.brandFocus,
      suggestedBgmMood: b.suggestedBgmMood ?? form.suggestedBgmMood,
      fontStyle: locked.has("fontStyle")
        ? form.fontStyle
        : (b.suggestedFontStyle ?? form.fontStyle),
      voiceSpeechRate: locked.has("voiceSpeechRate")
        ? form.voiceSpeechRate
        : (b.voiceSpeechRate ?? form.voiceSpeechRate),
      voiceVolume: locked.has("voiceVolume")
        ? form.voiceVolume
        : (b.voiceVolume ?? form.voiceVolume),
      transition: locked.has("transition") ? form.transition : (b.transition ?? form.transition),
      // 契约 v2:userInput 不再被编排产物覆写。旧行为是「拿 panelPrompts[0] 当生成提示词回填」——
      // 那是「导演产 prompt 文本」年代的产物;现在 prompt 由 ai 编译层从 panelScenes/panelMotions
      // 单点生成,userInput 回到它本来的身份:用户原话(导演输入 + 任务名兜底)。
      // 单图 LLM 负面词比默认更精准,回填 negativePrompt;多格走 negativePrompts 数组
      negativePrompt:
        b.negativePrompts && b.negativePrompts.length > 0
          ? b.negativePrompts[0]
          : form.negativePrompt,
      // P2d-1:摄影富字段(非空覆盖、空保旧,对齐 api ARRAY_FIELDS 语义)
      ...mergeBootstrapRichFields(form, b),
    };
    // 新分镜到达后最终对位:长度失配的旧镜组 per-shot 数组一律丢弃(2026-07-12 生产实证
    // 草稿 1 镜 panelScenes + 6 镜 lensMm 错位入库)。
    const aligned = alignPerShotArrays(next);
    setForm(aligned);
    recordAutoMotion(aligned.references);
    return aligned;
  };

  // 2. 海报一键成片初次装配 — 只拉海报缩略图/参考帧,**不跑 LLM**(optimize=false)。
  //    用户填意图点「融合海报优化」才跑 grid_detect;或自己写文案走「AI 优化文案」。
  useEffect(() => {
    if (!fromPoster) return;
    let cancelled = false;
    void videoService.getPosterTaskName(Number(fromPoster)).then((n) => {
      if (!cancelled) setPosterWorkName(n);
    });
    videoService
      .bootstrapFromPoster(Number(fromPoster), assetSeq ? Number(assetSeq) : undefined)
      .then((b) => {
        if (!cancelled) applyBootstrap(b);
      })
      .catch((e) => setCapsError(errorText(e)))
      .finally(() => !cancelled && setBootstrapping(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromPoster, assetSeq]);

  // 模版库点模板带 ?templateId= 进来:拉模板 → 预填**故事 brief**(目的/风格/基调/时长/故事模式)+ 推荐参数,
  // 并按 formFields 初始化填空值。userInput 不直接用 briefScript(留 {占位符}),由「一键」时填空替换后生成。
  useEffect(() => {
    if (!templateId) return;
    let cancelled = false;
    videoService
      .getTemplate(Number(templateId))
      .then((t) => {
        if (cancelled) return;
        setTpl(t);
        const fields = (t.formFields ?? []) as unknown as TemplateFormField[];
        const initVals: Record<string, string> = {};
        fields.forEach((f) => {
          if (f.key) initVals[f.key] = f.default ?? "";
        });
        setTemplateFieldValues(initVals);
        setForm((s) => ({
          ...s,
          source: "TEMPLATE",
          sourceRef: { templateId: Number(templateId) },
          name: t.name || s.name,
          provider: t.preferredProvider ?? s.provider,
          model: t.preferredModel ?? s.model,
          useAutoModel: !t.preferredModel,
          ratio: t.preferredRatio || s.ratio,
          durationSec: Math.min(15, Math.max(2, t.preferredDurationSec || s.durationSec)),
          resolution: t.preferredResolution || s.resolution,
          generateAudio: t.preferredGenerateAudio ?? s.generateAudio,
          // V46 故事配方:模版预设故事引擎结构化输入
          briefPurpose: t.briefPurpose ?? s.briefPurpose,
          briefStyle: t.briefStyle ?? s.briefStyle,
          briefTone: t.briefTone ?? s.briefTone,
          briefDuration: t.briefDuration ?? s.briefDuration,
          storyMode: t.storyMode ?? s.storyMode,
        }));
      })
      .catch((e) => setCapsError(errorText(e)));
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  // 海报一键成片:**编排也搬服务端**。不再客户端阻塞跑 grid_detect;建占位草稿(source=FROM_POSTER +
  // sourceRef + 意图 + storyMode,保留 anchorKey/firstFrameUrl)→ 走同一个 compose-and-generate Job →
  // 轮询。worker 据 sourceRef.fromPosterTaskId 走海报编排(storyMode 走故事引擎,否则忠于海报锁帧)。
  const handleRefuse = async () => {
    if (!fromPoster) return;
    await triggerComposeJob({
      ...form,
      source: "FROM_POSTER",
      sourceRef: {
        fromPosterTaskId: Number(fromPoster),
        fromPosterAssetSeq: assetSeq ? Number(assetSeq) : undefined,
      },
      userInput: posterIntent.trim(),
    });
  };

  // 3/4. 约束派生(P2-3/Coup-4):mode 推断 + 智能选型 + selectedCap + 手动模式联动收窄,
  // 整块抽到 useVideoProviderConstraints(见该 hook)。
  const { book: priceBook } = usePriceBook();
  const { hasPanelFrames, effectiveMode, effectiveModel, selectedCap } =
    useVideoProviderConstraints(caps, form, setForm);
  // OEM H2:引擎对客名走后端档位名(带 tier 才算新后端;老后端回落镜像表,不漏真名)。
  const selectedCapLabel = selectedCap
    ? videoProviderLabel(
        selectedCap.provider,
        selectedCap.model,
        selectedCap.tier ? selectedCap.label : null
      )
    : undefined;

  const setModelProvider = (provider: string, model: string) => {
    setForm((f) => ({ ...f, provider, model, useAutoModel: false }));
  };

  // ── 分镜 storyboard:派生 shots + 统一写回 form 平行数组 ──────────────
  // 契约 v2:基准数组 = panelScenes(它的长度就是镜数)。
  // ★两态收敛批:派生/写回全走 model/shotList 聚合层 —— 此前这里手写 9 条数组,
  //   剩下 13 条(运镜/景别/hero…)删镜不跟着删,全片镜头语言错位一格且无报错。
  const shots: PanelShotData[] = shotsFromForm(form);
  // 批 B1:逐镜参考的素材目录(动作库/人物物料/产品图现签)——只在真有对应参考时才发请求。
  const refCatalog = useReferenceThumbs(form);

  // M2 拍→镜映射:仿拍 ai 回传 panelBeats 按名归拍;学套路按时长就近;结构轨+挂拍标签共用。
  const shotBeatMap = useMemo(() => {
    if (!blueprint || shotsCountOf(form) === 0) return [];
    return mapShotsToBeats(
      blueprint.beats,
      form.panelBeats ?? null,
      form.panelDurations ?? null,
      shotsCountOf(form)
    );
  }, [blueprint, form]);

  /**
   * 节拍标签(结构轨/矮卡共用):蓝图路沿用「第N拍·拍名」;普通编排路用导演产的
   * narrativeRoles 翻人话(2026-08-10)—— 此前这条路恒 undefined,导演明明每单都产
   * 「钩子→蓄势→转折→收束→CTA」,用户却看不到这条片的讲法结构。
   */
  const storyBeatLabels = useMemo(() => {
    if (blueprint) {
      return shotBeatMap.map((bi) =>
        bi >= 0 ? `第${bi + 1}拍 · ${blueprint.beats[bi]?.label ?? ""}` : null
      );
    }
    const labels = shots.map((s) => narrativeRoleLabel(s.narrativeRole));
    return labels.some(Boolean) ? labels : undefined;
  }, [blueprint, shotBeatMap, shots]);

  // 黄金 3 秒横幅取值(hookPlan 是宽类型 Record<string, unknown>,这里收窄成串)。
  const hookPlanObj = form.hookPlan ?? null;
  const hookCopy = hookPlanObj && typeof hookPlanObj.copy === "string" ? hookPlanObj.copy : "";
  /**
   * ★visual 是 ai 侧视觉钩模板 key,直印就是「画面:SCREEN_PROOF」——内部枚举漏到对客面
   *   (2026-09-12 四刀 ①)。翻人话在 model/hookVisual.ts;未知枚举返 null,整段不渲染。
   */
  const hookVisual = hookVisualLabel(
    hookPlanObj && typeof hookPlanObj.visual === "string" ? hookPlanObj.visual : null
  );

  const writeShots = (next: PanelShotData[]) =>
    setForm((s) => ({ ...s, ...formPatchFromShots(next) }));

  const addShot = () => writeShots([...shots, emptyShot(form)]);

  /**
   * 幕二 master-detail 的选中态(null = 右栏显示全片视图)。
   * ★进幕二默认不选 —— 先看整体再点细节;编排刚回来时用户要判断的是「这条片对不对」,
   *   而不是「第 1 镜对不对」。
   */
  const [selectedShot, setSelectedShot] = useState<number | null>(null);
  /** 大图看图层看的是第几镜(与选中态分开:看图可跨镜翻,不该顺带改选中)。 */
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // 镜数变少时把越界的选中态收回全片视图(删掉正在看的那一镜 → 右栏不能停在空位上)。
  useEffect(() => {
    if (selectedShot !== null && selectedShot >= shots.length) setSelectedShot(null);
  }, [shots.length, selectedShot]);

  // 卡间「+ 在此插一镜」:index = 新镜落位(0 = 插到最前)。
  const insertShot = (index: number) => writeShots(insertShotAt(shots, index, emptyShot(form)));

  // 锚点:开了「主角锚点」优先用主角定妆图(所有镜锚定它,regen 不漂移);
  // 否则一键成片用海报图 key,其余用第 1 镜已出的图 → 后续镜 i2i 参考它保跨镜一致。
  //
  // ★2026-08-07:末位退到 previewKey(此前只看 firstFrameKey)。批 B2 之后 T2V 镜出的图**只写
  //   previewKey**(渲染键留空是刻意的:写了会把整个任务翻成 I2V),于是全 T2V 的编排里
  //   `shots[0].firstFrameKey` 恒为 undefined,锚点一直是空 —— 而「跨镜一致靠锚点」正是
  //   废掉 artDirection 时给出的替代方案,两条腿同时断,8 张图当然互不相干。
  //   服务端 anchorFor 同坑同修(VideoDraftOrchestrationService → VideoFramePlan.anchorFor),
  //   两边必须同口径:有草稿时走服务端那条,本地这条是无草稿兜底。
  const anchorFor = (i: number): string | undefined => {
    if (form.useProtagonistAnchor && form.protagonistAnchorKey) return form.protagonistAnchorKey;
    if (i === 0) return form.anchorKey ?? undefined;
    return form.anchorKey ?? shots[0]?.firstFrameKey ?? shots[0]?.previewKey ?? undefined;
  };

  const regenerateFrame = async (i: number) => {
    // 首帧是一张静帧,环境 + 动作合起来才描述得完整(契约 v2 后两者各自都只有十几个字,
    // 单给环境出的图里没有主体)。artDirection 已废 —— 跨镜一致靠 anchorKey 锚点。
    const sceneText = [shots[i]?.scene, shots[i]?.motion]
      .map((s) => (s ?? "").trim())
      .filter(Boolean)
      .join(",");
    if (!sceneText) {
      setSubmitError("先填本镜「环境」或「动作」再生成首帧");
      return;
    }
    setFramingSet((s) => new Set(s).add(i));
    setSubmitError(null);
    try {
      // 有草稿 → 走服务端重渲(草稿作 SoT,服务端写回 form_json);无草稿(罕见)→ 直出兜底。
      const { key, url } = draftIdRef.current
        ? await videoService.regenerateDraftFrame(draftIdRef.current, i)
        : await videoService.generateShotFrame({
            scenePrompt: sceneText,
            ratio: form.ratio,
            anchorKey: anchorFor(i),
          });
      const next = [...shots];
      // ★批 B2:出来的图落哪个字段,要跟服务端同一口径 —— 服务端只给非 T2V 镜写渲染键,
      //   前端却无脑写 firstFrameKey 的话,autosave 会把它again 落库,等于**绕过**那条规则:
      //   T2V 镜凭空有了渲染帧 → `hasPanelFrames` 把整个任务翻成 I2V。本地真跑当场抓到
      //   (服务端已改对,DB 里 panelFirstFrameKeys 照样被写上,元凶就是这一行)。
      next[i] =
        next[i].shotMode === "T2V"
          ? { ...next[i], previewKey: key, previewUrl: url }
          : {
              ...next[i],
              firstFrameKey: key,
              firstFrameUrl: url,
              previewKey: key,
              previewUrl: url,
            };
      writeShots(next);
    } catch (e) {
      setSubmitError(errorText(e));
    } finally {
      setFramingSet((s) => {
        const n = new Set(s);
        n.delete(i);
        return n;
      });
    }
  };

  /**
   * 一键重出全部缺图的镜(2026-08-10,全败/半败收尾入口)。并发发 job(服务端已按 draftId
   * 加写锁,并发写回不互盖);写回本地态用**函数式 setForm 按最新 shots 打补丁** ——
   * 不能复用 regenerateFrame 里 `[...shots]` 的闭包快照,并发完成时那是同一份旧数组,
   * 后完成的会把先完成的盖掉(与服务端修掉的是同一型 bug,前端态这份也得防)。
   */
  const regenerateAllMissingFrames = async () => {
    const draftId = draftIdRef.current;
    if (!draftId) return;
    const missing = shots
      .map((s, i) => ({ s, i }))
      .filter(
        ({ s }) =>
          !s.previewKey && !s.firstFrameKey && [s.scene, s.motion].some((t) => (t ?? "").trim())
      );
    if (missing.length === 0) return;
    setSubmitError(null);
    setFramingSet(new Set(missing.map((m) => m.i)));
    let failed = 0;
    let lastErr = "";
    await Promise.all(
      missing.map(async ({ i }) => {
        try {
          const { key, url } = await videoService.regenerateDraftFrame(draftId, i);
          setForm((f) => {
            const next = shotsFromForm(f);
            const shot = next[i];
            if (!shot) return f;
            next[i] =
              shot.shotMode === "T2V"
                ? { ...shot, previewKey: key, previewUrl: url }
                : {
                    ...shot,
                    firstFrameKey: key,
                    firstFrameUrl: url,
                    previewKey: key,
                    previewUrl: url,
                  };
            return { ...f, ...formPatchFromShots(next) };
          });
        } catch (e) {
          failed += 1;
          lastErr = errorText(e);
        } finally {
          setFramingSet((s0) => {
            const n = new Set(s0);
            n.delete(i);
            return n;
          });
        }
      })
    );
    if (failed > 0) {
      // 0901:去掉「多为出图渠道故障」的猜测(当天真机全灭的真凶是过期签名,按渠道指路
      // 把排障带偏)—— 有真实报错就原样透出,没有只说事实。
      setSubmitError(
        `${failed} 镜重出仍失败${lastErr ? `(最近一次失败原因:${lastErr})` : ""} —— 反复失败请把任务号发给运营排查`
      );
    }
  };

  const uploadFrameForShot = async (i: number, file: File) => {
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) {
      setSubmitError("首帧图仅支持 png/jpeg/webp");
      return;
    }
    setFramingSet((s) => new Set(s).add(i));
    setSubmitError(null);
    try {
      const { key, url } = await videoService.uploadFrame(file);
      const next = [...shots];
      // ★批 B2:用户**亲手传**的图必须算数。此前只写 firstFrameKey/Url,而 T2V 镜的首帧在
      //   渲染层根本不被消费(ai `mode_router` 要 shotMode=I2V 才吃)—— 净效果是:整个任务被
      //   `hasPanelFrames` 翻成 I2V,而这一镜照旧文生视频、传的图石沉大海。
      //   显式上传 = 明确要这张当起始画面 → 同时把该镜切成 I2V,镜卡标签也随之变成
      //   「起始画面 · 会用于出片」,所见即所得。
      next[i] = { ...next[i], firstFrameKey: key, firstFrameUrl: url, shotMode: "I2V" };
      writeShots(next);
    } catch (e) {
      setSubmitError(errorText(e));
    } finally {
      setFramingSet((s) => {
        const n = new Set(s);
        n.delete(i);
        return n;
      });
    }
  };

  const bindDraftIdToUrl = (draftId: number) => {
    const sp = new URLSearchParams(searchParams);
    sp.set("draftId", String(draftId));
    setSearchParams(sp, { replace: true });
  };

  // 轮询草稿 Job 状态(编排+出图共用):COMPOSING → GENERATING → DONE/PARTIAL/FAILED。
  // storyboard 首次出现时全量 hydrate 一次(把 LLM 编排的大纲灌回 form),之后只更新帧 key(别冲掉编辑)。
  // 服务端是 storyboard + 帧 key 的唯一写者(轮询期间前端 autosave 已 gated 关掉,避免 dual-writer)。
  const pollFrameGen = (draftId: number) => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    const tick = async () => {
      try {
        const d = await videoService.getDraft(draftId);
        const parsed = JSON.parse(d.formJson) as FormState;
        // W0:注入的选人实体是 characterId 的派生物,回载即剥离(出口再注入),
        // 否则会被产品面板当上传图展示、并在面板 onChange 整表覆盖时冲掉。
        parsed.entities = stripLibCharacterEntities(parsed.entities);
        const status = d.frameGenStatus ?? "RUNNING";
        setFrameGenStatus(status);
        setComposeFailKind(d.composeFailKind ?? null);

        const hasStoryboard = !!parsed.panelScenes && parsed.panelScenes.length > 0;
        if (hasStoryboard) {
          const keys = parsed.panelFirstFrameKeys ?? [];
          // 批 B2:预览图与首帧是两个字段,但**同一批签**(都是 video-frames/{tenant}/ 前缀,
          // presignFrames 认;分两次签 = 多打一趟网络还容易漏其中一边)。
          const previewKeys = parsed.panelPreviewKeys ?? [];
          const present = [...keys, ...previewKeys].filter((k): k is string => !!k);
          let urlMap: Record<string, string> = {};
          if (present.length > 0) {
            try {
              urlMap = await videoService.presignFrames([...new Set(present)]);
            } catch {
              // 重签失败不阻断,下轮再试
            }
          }
          const urls = keys.map((k) => (k ? (urlMap[k] ?? "") : ""));
          const previewUrls = previewKeys.map((k) => (k ? (urlMap[k] ?? "") : ""));
          if (!storyboardHydratedRef.current) {
            // 首次拿到 storyboard:全量灌回(大纲 + 已出帧)
            storyboardHydratedRef.current = true;
            const hydrated: FormState = {
              ...parsed,
              panelFirstFrameKeys: keys,
              panelFirstFrameUrls: urls,
              panelPreviewKeys: previewKeys,
              panelPreviewUrls: previewUrls,
            };
            setForm(hydrated);
            // ★指纹在**分镜产出的这一刻**取(见 composeSnapshot 声明处):这组分镜就是按
            //   这份输入编排出来的,之后任何偏离它的改动才叫「要重新编排」。
            setComposeSnapshot(composeInputDigest(hydrated));
            recordAutoMotion(parsed.references);
            // M2:蓝图路完成反馈(P0-2 §5-4)——「已按 N 拍结构出 M 镜」。
            if (blueprintComposeRef.current) {
              toast({
                title: `已按 ${blueprintComposeRef.current.beatsCount} 拍结构出 ${parsed.panelScenes?.length ?? 0} 镜`,
                description: "右侧结构轨可看拍→镜归属;每镜可改画面/口播后再确认渲染。",
              });
              blueprintComposeRef.current = null;
            }
          } else {
            // 已 hydrate:只更新帧,保留用户编辑
            setForm((s) => ({
              ...s,
              panelFirstFrameKeys: keys,
              panelFirstFrameUrls: urls,
              panelPreviewKeys: previewKeys,
              panelPreviewUrls: previewUrls,
              // packs W4:选了库人物 → 本地锚(人物主定妆图)优先,服务端自产定妆图不覆盖。
              protagonistAnchorKey: mergeAnchorFromServer(s, parsed.protagonistAnchorKey),
            }));
          }
        }
        // COMPOSING(还没 storyboard)只等;RUNNING/GENERATING 继续轮询
        if (status === "COMPOSING" || status === "GENERATING" || status === "RUNNING") {
          pollTimer.current = setTimeout(() => void tick(), 2500);
        } else {
          setFramingBatch(false); // DONE/PARTIAL/FAILED/COMPOSE_FAILED
        }
      } catch {
        // 轮询失败:停轮询,保留已显示;用户可刷新草稿续看
        setFramingBatch(false);
      }
    };
    void tick();
  };

  // 纯文案「AI 智能编排」:**编排也搬服务端**(两阶 LLM ~126s 慢)。前端:建占位草稿(只存 brief)→
  // 触发 compose-and-generate Job → 轮询。点完即可走人,编排+出图全在服务端,切走/刷新/关页不丢。
  // 编排 Job 内核(文字编排 + 模版一键 共用):建占位草稿(只存 brief、清旧 storyboard)→ 触发
  // 服务端「编排+出图」Job → 轮询。0 秒就有草稿、126s 窗口也被保护,点完即走。
  const triggerComposeJob = async (baseForm: FormState) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setComposing(true);
    setSubmitError(null);
    /**
     * 批⑫a:**清空之前**先留一份。下面 `setForm(placeholder)` 一执行,用户逐镜手改过的
     * 画面/口播/时长就没了 —— 成功了用「恢复上一版分镜」切回来,失败了在 catch 里自动回填。
     * 首次编排(还没分镜)拿到 null,槽保持空,恢复入口不出现。
     */
    const backup = captureStoryboardBackup(baseForm);
    if (backup) setStoryboardBackup(backup);
    try {
      const placeholder: FormState = {
        ...baseForm,
        // packs W4 选人:userInput 提交时追加逐字人物卡块(文本锁;输入框里用户看到的不变)。
        userInput: withCharacterSheet(baseForm.userInput, baseForm),
        // ★1027 不再拿租户名补 briefBrand:演示/代运营租户名≠产品品牌,补进去会被导演选成
        //   brandFocus,后期在第二段开头烧成品牌大字(测试机「销帮实战演示」压在栖暖保温杯片上)。
        //   品牌候选只走 fact pack 的产品树(产品名/别名);选不到就不烧品牌字。
        briefBrand: baseForm.briefBrand?.trim() ? baseForm.briefBrand : null,
        panelScenes: null,
        panelMotions: null,
        panelSubjectRefs: null,
        panelDurations: null,
        negativePrompts: null,
        voiceovers: null,
        voiceEmotions: null,
        // anchorKey(海报锚点)/ firstFrameUrl(海报参考图)保留 —— 海报路忠于海报要锁帧;文字路本就 null。
        // packs W4:选了库人物 → 锚是用户手选,编排不清(characterPick.composeResetAnchor)。
        ...composeResetAnchor(baseForm),
        panelFirstFrameKeys: null,
        panelFirstFrameUrls: null,
        storyBible: null,
        theme: null,
        panelBeats: null,
        // W4(2026-09-13):导演交代卡数据同 storyBible/theme 一族 ——「先清后写」,新一轮响应
        // 没给某字段时(mergeStoryboard 跳过 null)不许让上一轮的选料/审片残留、被当成本轮结果显示。
        // ★pinnedFactIds 不清:那是这一轮编排的**输入**(见 composeDirty.ts / storyboardBackup.ts)。
        selectedFacts: null,
        unusedFacts: null,
        estimatedDurationSec: null,
        factTexts: null,
        reviewScore: null,
        reviewVerdict: null,
        reviewMissingInputs: null,
        // 波2:上一轮编排「引擎实际用了哪张拍法」的记录也要清 —— 不清就会这样烂:
        // 第一轮引擎选了 A(写进 formJson),第二轮改成贴分镜脚本(引擎忠实拆解、不套拍法,
        // 响应里 formulaKey=null → mergeStoryboard 跳过 null 不覆盖)→ formJson 里 A 还在,
        // 详情页「本次设置」拿它当本轮结果显示。**用户选的 pickedFormulaKey 不清**(那是输入,不是结果)。
        formulaKey: null,
        formulaName: null,
        // 0924 车 C:上一轮导演判的大字档也是「结果」,先清后写(toneRegister 是输入,不清)。
        textOverlay: null,
      };
      setForm(placeholder);
      storyboardHydratedRef.current = false; // 等服务端编排出 storyboard 再 hydrate
      setAutoMotion({}); // 上一轮的「AI 配」标注不许挂到新镜组头上
      // M2 完成 toast 上下文:蓝图路编排完成报「已按 N 拍结构出 M 镜」。
      blueprintComposeRef.current = blueprint ? { beatsCount: blueprint.beats.length } : null;
      const res = await videoService.upsertDraft({
        draftId: draftIdRef.current ?? undefined,
        name: draftName(placeholder),
        // W0 事故修:选人实体随草稿上车(compose 在服务端读 formJson.entities,缺=选人白选)。
        // ★架构师全目的打开(2026-09-13 拍板;真单 5787265009665 实锤 storyMode=false 把
        //   两级编排的第一级整个关掉,口播只剩清单腔):非海报路一律 true——目的流由
        //   applyPurposeToForm 恒开,这里兜住自由创作/模板等没走目的映射的路;海报路保留
        //   「讲个故事/忠于海报」用户开关(同字段不同语义,不许被这里盖掉)。
        formJson: JSON.stringify({
          ...placeholder,
          // 0924 车 C:导演在 compose 阶段按 resolve_tone_register(toneRegister, industryCode) 定档。
          //   toneRegister 已是生效档(见上方默认值 effect);行业码 form 里通常没有,补租户行业。
          //   草稿回填后 form 也会带上它、建任务随之发出 —— ai 视频线的 industryCode 只用于推调性档。
          industryCode: placeholder.industryCode ?? tenantIndustryCode,
          entities: entitiesWithCharacter(placeholder),
          storyMode: fromPoster ? placeholder.storyMode : true,
        }),
      });
      draftIdRef.current = res.draftId;
      setCurrentDraftId(res.draftId);
      bindDraftIdToUrl(res.draftId);
      setFramingBatch(true);
      setFrameGenStatus("COMPOSING");
      await videoService.composeAndGenerateDraft(res.draftId);
      pollFrameGen(res.draftId);
    } catch (e) {
      setSubmitError(errorText(e));
      setFramingBatch(false);
      setFrameGenStatus("FAILED");
      /**
       * 批⑫a:编排没起来 = 一镜都没产出,但 placeholder 早把上一版清空并 setForm 了 ——
       * 不回填的话,**一次失败的编排顺手吃掉用户手改过的整份分镜**(此前正是如此:
       * catch 里只 setSubmitError,分镜就那么没了,连告知都没有)。
       */
      if (backup) {
        setForm((s) => applyStoryboardBackup(s, backup));
        // 已经切回去了,槽里再留同一份就变成「恢复到自己」——那个键点了什么也不会发生。
        setStoryboardBackup(null);
        // 回填的这版就是当前版:别让残留的轮询把它当「新 storyboard」再全量 hydrate 一次。
        storyboardHydratedRef.current = true;
        toast({
          title: "编排失败,已恢复你之前的分镜",
          description: "分镜没有丢。想再试一次就点「重新编排」。",
        });
      }
    } finally {
      inFlightRef.current = false;
      setComposing(false);
    }
  };

  /**
   * 批⑫a 重排安全网:**已有分镜时,任何一次编排都先过确认弹窗**。
   *
   * `triggerComposeJob` 进门第一件事就是把 panelScenes/panelMotions/voiceovers/panelDurations/
   * 首帧…… 整组置 null 再 setForm —— 用户手改过的分镜在点下去的瞬间蒸发,此前既无确认也无撤销。
   * 而入口有三个(主按钮、摘要条那颗 h-7 小按钮、模版填空面板的「生成」),闸只放在其中一个
   * 就等于没闸,所以闸放在**所有入口共用的这一层**:三处都改调 guardedCompose。
   *
   * ★首次编排(还没分镜)直接跑:那时没有任何东西会被覆盖,弹窗只是噪音。
   * ★`hasStoryboard` 在下方(渲染派生区)声明 —— 这个函数只在事件回调里被调用,
   *   彼时闭包变量早已初始化,不存在 TDZ。
   */
  const guardedCompose = (run: () => Promise<void>) => {
    if (!hasStoryboard) {
      void run();
      return;
    }
    pendingComposeRef.current = run;
    setRecomposeConfirmOpen(true);
  };

  const handleCompose = async () => {
    if (copyRealBlock) {
      setSubmitError(copyRealBlock);
      return;
    }
    if (!form.userInput.trim()) {
      setSubmitError("先填产品/公司介绍,AI 才能编排分镜");
      return;
    }
    // 波3-⑦:目的**不再必填**。引擎 `select_playbook` 本来就写着「未命中/空 → 产品带货兜底」,
    // 从不要求 purpose 必填 —— 这道门是 console 单方面加的,拦掉的是「给一个框就想开工」的人。
    // ★取消必填不等于静默:没选时来源条会明写「未选目的 → 按『产品带货』编排」并给一键改
    // (见 sourceBarData 的 free/purpose 分支)。引擎是「没给就当带货」不是「从文案推目的」,
    // 装作推断出来的才是概览比实际乐观。
    guardedCompose(() => triggerComposeJob(form));
  };

  // 模版一键成片:填空替换进 briefScript → userInput,brief 已由模版预设,直接触发 Job。
  const handleComposeTemplate = async () => {
    if (!tpl) return;
    const fields = (tpl.formFields ?? []) as unknown as TemplateFormField[];
    const missing = fields.filter((f) => f.required && !(templateFieldValues[f.key] ?? "").trim());
    if (missing.length > 0) {
      setSubmitError(`请先填写:${missing.map((f) => f.label).join("、")}`);
      return;
    }
    const filledInput = fillBriefScript(tpl.briefScript, templateFieldValues);
    // 模版路在幕二也够得着(备料台「改设定」展开后填空面板连同「生成」按钮一起回来),
    // 所以它同样要过重排闸 —— 少这一处,那条路上的分镜照样静默蒸发。
    guardedCompose(() => triggerComposeJob({ ...form, userInput: filledInput }));
  };

  // ───── 草稿:保存 / 自动保存 / 恢复 ─────
  const saveDraft = async (silent: boolean): Promise<boolean> => {
    const name = videoDraftName(form);
    setSavingDraft(true);
    try {
      const res = await videoService.upsertDraft({
        draftId: currentDraftId ?? undefined,
        name,
        // W0 事故修:同 compose 占位草稿——选人实体随存草稿上车。
        formJson: JSON.stringify({ ...form, entities: entitiesWithCharacter(form) }),
      });
      setCurrentDraftId(res.draftId);
      if (!silent) toast({ title: "草稿已保存", description: "可在「我的草稿」继续编辑" });
      return true;
    } catch (e) {
      if (!silent) toast(errorToast(e, { fallbackTitle: "保存草稿失败" }));
      return false;
    } finally {
      setSavingDraft(false);
    }
  };

  const draftName = (s: FormState) => videoDraftName(s);

  /**
   * 「恢复上一版分镜」(批⑫a)——「重新编排」的撤销键。
   *
   * ★**互换**而不是单向还原:被换下去的这一版立刻进槽,所以能来回切。新一版不一定比旧的好,
   *   而"好不好"要两版都看过才知道 —— 只给单向还原的话,切回旧版就等于把新版删了,
   *   用户在没看够的情况下被迫做一次不可逆选择。
   * ★立刻 upsertDraft 持久化,不等 autosave 那 2.5s 防抖:恢复完顺手关页/切走是常见动作,
   *   落在防抖窗里就等于没恢复(而用户以为恢复了)。持久化失败要说出来,不许静默。
   */
  const handleRestoreStoryboard = async () => {
    const backup = storyboardBackup;
    if (!backup) return;
    const swapped = captureStoryboardBackup(form);
    const next = applyStoryboardBackup(form, backup);
    setStoryboardBackup(swapped);
    setForm(next);
    // 回填的这版就是当前版(理由同 triggerComposeJob 失败路)。
    storyboardHydratedRef.current = true;
    setSavingDraft(true);
    try {
      const res = await videoService.upsertDraft({
        draftId: draftIdRef.current ?? currentDraftId ?? undefined,
        name: draftName(next),
        // W0 事故修:同 saveDraft —— 选人实体随存草稿上车。
        formJson: JSON.stringify({ ...next, entities: entitiesWithCharacter(next) }),
      });
      setCurrentDraftId(res.draftId);
      toast({
        title: "已恢复上一版分镜",
        description: swapped
          ? "再点一次「恢复上一版分镜」可切回刚才那版。"
          : "台本图与逐镜口播都跟着切回来了。",
      });
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "分镜已切回,但没能存进草稿" }));
    } finally {
      setSavingDraft(false);
    }
  };

  // 自动存:有 storyboard(编排产出 / 付费帧)后,storyboard/帧变动 2.5s 防抖静默 upsert。
  // 用 effect 而非在 handleCompose 里调 —— effect 跑在 setForm 之后,拿到的是最新 form(避开 stale 闭包),
  // 也天然覆盖「编排后 / 逐镜重生后 / 用户改分镜后」。不监听 userInput → 打字不建草稿。
  // **出图进行中(framingBatch)不自动存** —— 服务端是首帧 key 的唯一写者,前端轮询的 setForm 若再
  // 触发 autosave 会把服务端刚写的帧冲掉(dual-writer)。出图结束(非 RUNNING)再恢复自动存。
  useEffect(() => {
    if (!form.panelScenes || form.panelScenes.length === 0) return;
    if (framingBatch) return;
    const t = setTimeout(() => void saveDraft(true), 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    framingBatch,
    form.panelScenes,
    form.panelMotions,
    form.voiceovers,
    form.panelDurations,
    form.panelFirstFrameKeys,
  ]);

  // VID-UX-1:白纸进「新建视频」时查该用户有没有**在途编排/出图**的草稿 —— 有就给
  // 「继续上次的编排」恢复入口。此前横幅「关页回来自动续看」只在从草稿箱点回那份草稿时
  // 成立,重进新建页是白纸,用户以为编排丢了。listDrafts 服务端按 租户+当前用户 双钥过滤。
  const [resumableDraft, setResumableDraft] = useState<VideoDraftDto | null>(null);
  const [resumeDismissed, setResumeDismissed] = useState(false);
  useEffect(() => {
    if (searchParams.get("draftId") || fromPoster || templateId) return;
    let cancelled = false;
    videoService
      .listDrafts()
      .then((ds) => {
        if (!cancelled) setResumableDraft(pickResumableDraft(ds));
      })
      .catch(() => {
        /* 拉不到列表就不出横幅 —— 恢复入口是加分项,不许因它挡住新建 */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 恢复:URL ?draftId= → 拉草稿 → 解析 form → 按永久首帧 key 重签 URL → 灌回 form(只跑一次)。
  useEffect(() => {
    const did = searchParams.get("draftId");
    if (!did || draftRestoredRef.current || fromPoster || templateId) return;
    draftRestoredRef.current = true;
    videoService
      .getDraft(Number(did))
      .then(async (d) => {
        const parsed = JSON.parse(d.formJson) as FormState;
        // ★批 B2:预览图 key 也要在这条路上重签。轮询那条路签了、恢复这条路没签 =
        //   「同一件事两处各写一份」,净效果是**草稿重开后预览图全空**(图明明在 MinIO 里)。
        const keys = [
          ...(parsed.panelFirstFrameKeys ?? []),
          ...(parsed.panelPreviewKeys ?? []),
          parsed.protagonistAnchorKey ?? null,
          parsed.anchorKey ?? null,
        ].filter((k): k is string => !!k);
        if (keys.length > 0) {
          try {
            const urls = await videoService.presignFrames([...new Set(keys)]);
            if (parsed.panelFirstFrameKeys)
              // 重签不到的镜用空串(卡片按"未生成"处理,可重生);保持与 keys 同序
              parsed.panelFirstFrameUrls = parsed.panelFirstFrameKeys.map((k) =>
                k ? (urls[k] ?? "") : ""
              );
            if (parsed.panelPreviewKeys)
              parsed.panelPreviewUrls = parsed.panelPreviewKeys.map((k) =>
                k ? (urls[k] ?? "") : ""
              );
          } catch {
            // 重签失败不阻断,首帧缩图可能裂,用户可重生
          }
        }
        setForm(parsed);
        setCurrentDraftId(Number(did));
        draftIdRef.current = Number(did);
        const st = d.frameGenStatus ?? "IDLE";
        setFrameGenStatus(st);
        setComposeFailKind(d.composeFailKind ?? null);
        // 已有 storyboard 就标记已 hydrate(续轮询时只更新帧不冲掉);COMPOSING 占位草稿还没 storyboard。
        storyboardHydratedRef.current = !!parsed.panelScenes && parsed.panelScenes.length > 0;
        // 草稿重开:这组分镜是按草稿里存着的这份输入编排的 → 以它为基准判 stale。
        // 不设的话 snapshot 恒空,重开草稿后改什么都不提示重编排(P3 在这条路上复发)。
        if (storyboardHydratedRef.current) setComposeSnapshot(composeInputDigest(parsed));
        // 切走时 Job 还在跑(编排中 / 出图中)→ 回来续轮询(服务端没停,会渐次填满)
        if (st === "COMPOSING" || st === "GENERATING" || st === "RUNNING") {
          setFramingBatch(true);
          pollFrameGen(Number(did));
        }
      })
      .catch((e) => {
        const msg = e instanceof Error ? e.message : String(e);
        // ★「草稿不存在」要说清它去哪了(2026-08-07 走查):出片**成功**后 api 会自动清草稿
        //   (`MediaCompletionService.cleanupDraftOnSuccess`,与提交时"先保留可重试"是配套的)。
        //   用户拿着出片前的旧链接回来,看到光秃秃一句「草稿不存在」+ 一张空白新建页,
        //   读起来像"我的东西丢了"——其实是正常归档,而且成片就在作品列表里。
        setSubmitError(
          /草稿不存在|不存在|not\s*found/i.test(msg)
            ? "这份草稿已归档:它对应的视频出片成功后会自动清理。成片在「作品」列表里可以找到;要改就在这一页重新填,不影响已出的片。"
            : errorText(e)
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCustomBgmUpload = async (f: File) => {
    if (!/^audio\/(mpeg|mp3)$/.test(f.type)) {
      setSubmitError("仅支持 audio/mpeg (mp3)");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setSubmitError("BGM 文件需 ≤10MB");
      return;
    }
    setUploadingBgm(true);
    setSubmitError(null);
    try {
      const { objectKey, url } = await videoService.uploadCustomBgm(f);
      setCustomBgmFile(f);
      setCustomBgmUrl(url);
      setForm((s) => ({ ...s, bgmId: null, customBgmObjectKey: objectKey }));
    } catch (e) {
      setSubmitError(errorText(e));
    } finally {
      setUploadingBgm(false);
    }
  };

  // 有效总时长:多镜 = 各镜之和;未编排但选了时长档 = 该档目标秒数;都没有 = form.durationSec。
  // ★2026-07-29 收到 effectiveDuration.ts 单一真源 —— 原来这里**不看时长档**直接回落
  // form.durationSec(默认 5),而积分估算那边优先用档位秒数 → 同一页面右上显 5s、积分按 15s 算
  // (测试反馈 bug 9)。徽标/高级选项摘要/积分三处现在共用同一个函数。
  const effectiveDuration = useMemo(() => effectiveDurationSec(form), [form]);

  /**
   * ★ 屏幕上的钱**改由后端报价口现算**(`dryRun: true`):视频 = `video.render` × 秒数。
   *
   * `useVideoCreditEstimate` 早已不是钱的出处(0926 起连「张数」也不再用:出片条不再计台本图,
   * 见下方 submitCredits)。当初改走报价口同时拆掉了写在那个 hook 顶上的两条引信:
   *   ① 它把折算后的秒数先取整(3 秒那一镜高报 19%);
   *   ② 规格档单价里已含分辨率系数,它会再乘一次 ⇒ 谁把它接到价目表上就双重打折,
   *      **方向是往少了报**。现在它拿不到价目表,也就不可能被那么接。
   *
   * 取不到价 → null → 不显示数字(不退回那个 hook 的数,那等于又留一个出处)。
   */
  const videoQuoteParams =
    effectiveDuration > 0
      ? (() => {
          const variantKey = priceBook.variantByModel(
            SKU.video,
            effectiveModel.model,
            form.resolution
          );
          return variantKey ? { skuCode: SKU.video, variantKey, qty: effectiveDuration } : null;
        })()
      : null;
  const { credits: quotedVideoCredits } = useQuotePreview(videoQuoteParams);
  /**
   * 每张台本图单价 —— 问**首帧的结算 SKU** `video.frame/default`(0926 修:此前借
   * `poster.image/openai_2K` 报 66,实扣走 video.frame = 90,少报 36%)。首帧恒 2K
   * (ai `frame_gen._TIER`)由后端报价口自己认,前端不拼档位。说明文字与报价同一个出处。
   */
  const perFrameCredits = priceBook.unit(SKU.videoFrame, "default");
  // 1006:手选文生 → 台本图那句话改说「各镜文生、不出台本图」(api 5599e4c4 强制文生;frameRuleCaption)
  const manualTextOnly = isManualTextOnly({
    useAutoModel: form.useAutoModel,
    generationMode: form.generationMode,
    supportedGenerationModes: selectedCap?.supportedGenerationModes,
  });
  // 1006:手选文生且没勾「每镜都出」→ 最终不出台本图,编排按钮别再提台本图
  const composeFrames = composeMentionsFrames({
    manualTextOnly,
    previewFrames: form.previewFrames,
  });
  /**
   * 出片条「生成视频」的预估 = **只有成片费**(0926 修)。
   *
   * ★此前 = 成片 + 台本图(`video.frame` × 预估张数)。可出片条只在幕二(已编排)出现,
   *   台本图在编排时已逐张扣过,点生成视频只扣成片 —— 演示机 r1 报 1491 扣 1425、r2 报 3048
   *   扣 2850,多报的正是已扣的那几张。判据见 `VideoSubmitBar.noprice.test.tsx`。
   */
  const submitCredits = quotedVideoCredits;
  /**
   * 「不编排直接出片」那条的预估 —— **也走报价口**,不能拿 total 顶替:
   * 裸发根本不出台本图,total 里那笔 frame 是凭空多算的(这条语义原样保留)。
   */
  const bareSecs = bareSendDurationSec(form, bareSendMaxSec(selectedCap?.durationRange));
  const { credits: quotedBareSend } = useQuotePreview(
    videoQuoteParams && bareSecs > 0 ? { ...videoQuoteParams, qty: bareSecs } : null
  );

  /**
   * ★1004 T4:自由创作选 30s(或 1080P)时智能选型会从默认档升到装得下的档(演示机:极速 15s 1425 →
   *   旗舰 30s 7800),原来没有任何提示。这里说清原因,并把「原默认档最长能出的那段」也问一次价,
   *   两个数都来自报价口,前端不写数。手动选型 / 没升档 → 不出。
   */
  const autoUpgrade =
    // 只有直发(未定时长档)才会因单段时长升档;展示只在自由流的 freeCta 里
    caps && form.useAutoModel && !form.briefDuration
      ? autoUpgradeNotice({
          caps,
          mode: effectiveMode,
          generateAudio: form.generateAudio,
          durationSec: bareSecs,
          resolution: form.resolution,
          effective: effectiveModel,
        })
      : null;
  const { credits: quotedBaselineSend } = useQuotePreview(
    autoUpgrade
      ? (() => {
          const variantKey = priceBook.variantByModel(
            SKU.video,
            autoUpgrade.from.model,
            form.resolution
          );
          const secs = Math.min(bareSecs, autoUpgrade.from.maxSec);
          return variantKey && secs > 0 ? { skuCode: SKU.video, variantKey, qty: secs } : null;
        })()
      : null
  );

  /**
   * 「AI 智能编排」按钮旁的花费说明 —— 与军师确认卡同一个 ai 侧常量出处(见
   * `useComposeEstimate` 头注)。区间措辞复用 `advisorQuote.priceText()`,不新写一份规则。
   * ★还没拉回来(`failed=false` 且没数)→ `null`,按钮旁暂不显示数字;真拉挂了
   *   (`failed=true`)→ 兜底句,**不再整句消失**(0904:用户反馈「不要让用户觉得免费」,
   *   见 `composeEstimateCaption` 头注)。1006 起按钮本体只放动作名,价钱只在按钮旁/下小字。
   */
  const {
    low: composeEstLow,
    high: composeEstHigh,
    failed: composeEstFailed,
  } = useComposeEstimate();
  const composeEstimateText = composeEstimateCaption(
    composeEstLow,
    composeEstHigh,
    composeEstFailed
  );

  /**
   * 幕二右栏的真 prompt 预览 —— 发的是 `buildTaskRequest` 组的**同一份**请求体
   * (与下面 onSubmit 里出片发的逐字相同),由 api 转给 ai 的 `preview_chunk_prompts`,
   * 而那边复用出片路自己的 `chunk_shots` / `compile_chunk_prompt`。三层各自同源,
   * 右栏显示的才真的是「会发出去的那条」而不是一条像它的。
   *
   * 只在幕二开(enabled):幕一还没有分镜,没什么可编译。
   */
  const compilePreview = useCompilePreview(
    { form, effectiveModel, effectiveMode, sceneKey: sceneKeyForSubmit, currentDraftId },
    !!form.panelScenes && form.panelScenes.length >= 1
  );

  const onSubmit = async () => {
    // V-10(0811 反馈「点了 3 次才开始制作」):在途期间重复点击一律吞掉 ——
    // createTask 慢返回时用户会再点,没有这道闸就是重复建任务。
    if (inFlightRef.current) return;
    const isMultiShot = !!(form.panelScenes && form.panelScenes.length >= 2);
    if (!isMultiShot && !form.userInput.trim()) {
      setSubmitError("请填写文案描述");
      return;
    }
    // 0813:选了真人人物 → 定妆图进引擎必被肖像审核拒(ai 侧 72h 内最高频的一类失败)。
    // 提交前就拦住,别让人白等一趟出片再看失败文案。两条提交路(编排后出片 / 直接出片)
    // 都汇到这里,一道闸够。
    const realPersonBlocked = realPersonBlockReason(form) ?? copyRealBlock;
    if (realPersonBlocked) {
      setSubmitError(realPersonBlocked);
      return;
    }
    // 多镜:只有 **I2V 镜**(需锚首帧)缺首帧才拦 —— T2V 镜本就不出首帧(纯文生视频),不算缺。
    // 拦住「I2V 镜缺首帧」,提示补齐(点该镜「生成首帧」或上传)。
    if (form.panelScenes && form.panelScenes.length >= 1) {
      const missing = form.panelScenes
        .map((_, i) => i)
        .filter((i) => form.shotMode?.[i] !== "T2V" && !form.panelFirstFrameKeys?.[i]);
      if (missing.length > 0 && missing.length < form.panelScenes.length) {
        setSubmitError(
          `镜 ${missing.map((i) => i + 1).join("、")} 还没生成首帧 — 这些镜要锚首帧保持风格一致。点这些镜的「生成首帧」补齐(或「上传替换」)再提交。`
        );
        return;
      }
    }
    // 0924 车 B:自由创作素材行 —— 选了模式没传图 / 参考图配了不认参考的引擎,提交前说人话。
    if (isFreeFlow) {
      const materialBlocked = freeMaterialBlockReason(form, effectiveMode, effectiveModel.provider);
      if (materialBlocked) {
        setSubmitError(materialBlocked);
        return;
      }
    }
    // storyboard 每镜有自己的首帧(panelFirstFrameUrls),不需要全局 firstFrameUrl;只有
    // 非 storyboard 的单镜 I2V 才要求全局首帧。
    if (effectiveMode !== "T2V" && !form.firstFrameUrl && !hasPanelFrames) {
      setSubmitError("图生视频模式需上传参考首帧");
      return;
    }
    if (effectiveMode === "I2V_FIRST_LAST" && !form.lastFrameUrl) {
      setSubmitError("首尾帧模式需要参考末帧");
      return;
    }
    // 有首帧/海报却落到 T2V(手动选了只支持文生视频的模型)= 会忽略首帧凭空生成。
    if (effectiveMode === "T2V" && form.firstFrameUrl) {
      setSubmitError(
        "已有参考首帧/海报时不能用文生视频 —— 会忽略首帧凭空生成。请在「高级」里换能图生视频的档位(如「专业·综合」或「标准·图生视频」)。"
      );
      return;
    }
    // 尾句裁切提交明示(0817 拍板):ai 编排层会按 round(镜时长×5.85) 字硬裁口播
    // (#18,判据与 ai multi_shot._cap_voiceovers_by_duration 同源)。用户现象是
    // 「最后一句没念」以为是 bug —— 提交前明示裁点,知情同意后照裁。闸就是弹窗本身:
    // 唯一能绕过它的入口是弹窗里那颗「知道了,照此提交」,它必经 submitCreateTask。
    const voCuts = predictVoiceoverCuts(form.voiceovers, form.panelDurations);
    if (voCuts.length > 0) {
      setVoCutNotices(voCuts);
      return;
    }
    await submitCreateTask();
  };

  const submitCreateTask = async () => {
    setSubmitError(null);
    inFlightRef.current = true;
    setSubmitting(true);
    try {
      // ★组包收进 `buildTaskRequest` 单点(2026-08-06):幕二右栏的 prompt 预览要发**同一份**
      //   请求体给 ai 编译,两边各组一套就会显示一条不会真发出去的 prompt(见该模块头注)。
      // ★ 批 L:提交这一刻落一张**正式报价单**,单号随建单请求带给后端绑到 action_id。
      //   规格档 key 从价目表**反查**(model + 分辨率),不按命名规则拼。
      //   报价失败**不阻断提交**(返 null):把「没有报价单」修成「根本不能创作」,
      //   后者的用户成本高一个量级。没有单的这一单会在后端落 WARN 并被验收判据逮到。
      //   ⚠️ 已知缺口:页面上显示的预估仍来自 `useVideoCreditEstimate`,**不是这张单的数**
      //      (见那个 hook 顶上的两条)。绑单解决的是「按哪个价扣」,不是「看到的和扣的是否同一个数」。
      const videoQuoteId = await acquireQuoteId(
        (() => {
          const variantKey = priceBook.variantByModel(
            SKU.video,
            effectiveModel.model,
            form.resolution
          );
          return variantKey && effectiveDuration > 0
            ? { skuCode: SKU.video, variantKey, qty: effectiveDuration }
            : null;
        })()
      );
      const { taskId } = await videoService.createTask(
        buildTaskRequest({
          form,
          effectiveModel,
          effectiveMode,
          sceneKey: sceneKeyForSubmit,
          currentDraftId,
          // 直发单镜上限跟模型走(0824:2.5 直发能出 30s,别再一律钳 15)
          bareSendMaxSec: bareSendMaxSec(selectedCap?.durationRange),
          quoteId: videoQuoteId,
        })
      );
      // 改造(2026-06-17):**不再**在建任务时秒删草稿。出片常因单镜内容审核失败只出部分镜(PARTIAL),
      // 若此刻就删草稿,用户既没拿到成片、又丢了草稿+首帧无法重试 =「点一下啥都没了」。保留草稿 → 可改
      // 提示词重渲失败镜或整单重试;成片满意后由用户手动删(后续接「出片真正成功」回调自动清以免堆积)。
      // UX-2 (2026-05-30) — 提交成功即时反馈,跳详情前先确认(出片链路长,先安抚)。
      toast({
        title: "已提交生成",
        description: "视频生成中,详情页会实时更新进度;草稿已保留,可随时回来重试或修改。",
      });
      navigate(`/ai-video/tasks/${taskId}`);
    } catch (e: unknown) {
      const msg = errorText(e);
      // V-09(0811 反馈):提交失败自动落一份草稿 —— 用户失败后往往直接离开页面,
      // 回来发现全要重填。只有真存上了才在文案里承诺(saveDraft 静默失败不许说谎)。
      const kept = await saveDraft(true);
      setSubmitError(
        kept ? `${msg} —— 内容已自动存为草稿,离开页面也不会丢,可在「我的草稿」找回。` : msg
      );
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  };

  if (capsError) {
    return (
      <>
        <div className="w-full">
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            加载 capabilities 失败:{capsError}
          </div>
        </div>
      </>
    );
  }
  if (!caps)
    return (
      <>
        <div className="w-full">加载中...</div>
      </>
    );

  /**
   * ═══ 两幕(2026-08-06 重排)══════════════════════════════════════════════════
   * 页面结构复刻引擎结构:引擎是 compose(导演台产分镜)→ generate(出片)两阶,
   * 页面就是两幕,**每一幕只出现该幕会被消费的输入**。
   *   幕一 备料台:目的 / 格式 / 文案 + 素材 → 一颗「AI 智能编排」。无右栏。
   *   幕二 分镜台:摘要条 + 分镜网格 + 声音字幕 + 右栏 cockpit「生成视频」。
   * 重排前这两幕挤在一条 17 块的垂直流里,于是出片期参数(声音/字幕/BGM)排在编排按钮
   * 下方与风格/拍法平级 —— 它们在 ComposeRequest 里根本不存在。
   */
  // 镜数判据单点(批⑫a 抽进 model/storyboardBackup —— 快照/恢复入口/重排闸共用同一条)。
  const hasStoryboard = hasStoryboardShots(form);
  // 「自由外壳」:海报流已有参考图、模版流走填空,两者都不给设定与素材(同 resolveVideoSource)。
  const freeShell = !fromPoster && !templateId;
  // 备料台:幕一恒开;幕二默认收起,由摘要条「改设定」展开(D4:编排后仍允许改)。
  const deckVisible = !hasStoryboard || deckOpen;
  const deckLocked = composing || framingBatch;
  const stale = isComposeStale(composeSnapshot, form);
  /**
   * A2 目的锁定:自由流选定了有效目的(不含蓝图/海报/模板 —— 那些目的另有来路)时,
   * 目的进入「已锁定」态 —— 顶部出「拍摄任务书」横幅、备料台目的行退场。
   * ★只认自由外壳 + 无蓝图:蓝图态目的由蓝图公式定(showPurpose 本就关),海报/模板同理。
   */
  const lockedPurpose = freeShell && !blueprint ? briefPurposeOf(form.briefPurpose) : null;

  /**
   * D 批(2026-09-13,设计稿 7d586023 V4)—— 幕一(!hasStoryboard)三态各自的布局:
   *   · 目的流 isPurposeFlow:选定了目的(横幅锁定)→ **两栏**(左输入 + 右「这条片」摘要/机器/CTA);
   *   · 自由创作 isFreeFlow:自由外壳无目的无蓝图 → **独立精简页**(不穿目的壳,直发优先);
   *   · 其余(蓝图/海报/模板)→ 保持现状单列(current layout,本批不动)。
   * 幕二(hasStoryboard)整块不动。
   */
  const isPurposeFlow = !!lockedPurpose && !hasStoryboard;
  const isFreeFlow = freeShell && !blueprint && !lockedPurpose && !hasStoryboard;

  /** ③ 内容区:五来源各自的形态(自由文案 / 模版填空 / 海报意图)。 */
  const intakeContent =
    srcUi.showTemplateFill && tpl ? (
      <VideoTemplateFillPanel
        template={tpl}
        values={templateFieldValues}
        onChange={(k, v) => setTemplateFieldValues((m) => ({ ...m, [k]: v }))}
        onGenerate={handleComposeTemplate}
        busy={deckLocked}
      />
    ) : fromPoster ? (
      <div className="space-y-2">
        <Label htmlFor="poster-intent" className="text-[13.5px] font-semibold">
          想做个什么样的视频?(意图选填)
        </Label>
        <p className="text-2xs text-muted-foreground">
          海报已作为参考帧载入(见上方缩略图)。写下你的意图(如「突出限时促销」「走温情路线」),AI
          结合海报 + 你的意图生成。留空 = 纯按海报发挥。
        </p>
        {/* 讲故事 vs 忠于海报:故事=据海报主体/卖点搭故事弧(分镜各异);忠于海报=海报图锁帧让它动起来 */}
        <OptionChipGroup label="海报成片方式">
          {[
            { v: true, label: "讲个故事", hint: "据海报内容编一个有主角的小故事" },
            { v: false, label: "忠于海报", hint: "锁住海报版式,让它动起来" },
          ].map((o) => (
            <OptionChip
              key={String(o.v)}
              radio
              active={form.storyMode === o.v}
              tabIndex={form.storyMode === o.v ? 0 : -1}
              disabled={deckLocked}
              title={o.hint}
              onClick={() => setForm((s) => ({ ...s, storyMode: o.v }))}
            >
              {o.label}
            </OptionChip>
          ))}
        </OptionChipGroup>
        <Textarea
          id="poster-intent"
          rows={3}
          placeholder="例如:强调限时优惠 + 节奏快一点,突出「3 秒搞定」的卖点"
          value={posterIntent}
          disabled={deckLocked}
          onChange={(e) => setPosterIntent(e.target.value)}
          maxLength={2000}
          className="resize-none bg-background"
        />
      </div>
    ) : (
      <div className="space-y-2">
        <Label htmlFor="user-input" className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          {/* BPWB-1:引导跟来源走(用户反馈「想拍什么的提示还是原来的」)。蓝图态这框只装
              用户自己的产品 —— 名字就叫它该装的东西;幕一带 ② 与上方「① 换成你的产品」成序。 */}
          <span className="text-[13.5px] font-semibold text-foreground">
            {blueprint ? (hasStoryboard ? "介绍你的产品" : "② 介绍你的产品") : "想拍什么"}
          </span>
          <span className="text-2xs font-normal text-muted-foreground">
            {blueprint
              ? fidelityMode === "copy"
                ? "画面结构照原片来,口播和字幕按这里写的重写"
                : "只借结构骨架 —— 写你自己的产品,画面 AI 自由创作"
              : isFreeFlow
                ? // F 批:自由创作 = 文案直发给视频模型(不经 AI 编排),副标题点破这层;
                  // Z9d 0926(0812 反馈):顺带说清配音/字幕/语速在目的流(单源 freeFlowHints)。
                  FREE_FLOW_INTAKE_HINT
                : "写产品、卖点、场景,想到什么写什么"}
          </span>
          {/* D 批「想拍什么×时长联动」:选定目的的两栏流,标题旁按当前时长档内容位数给写作量提示。 */}
          {isPurposeFlow && intakeContentBeats != null && (
            <span data-testid="intake-beats-hint" className="text-2xs font-normal text-primary/80">
              这个时长讲 {intakeContentBeats} 个点 · 建议写 {intakeContentBeats} 个卖点
            </span>
          )}
        </Label>
        <Textarea
          id="user-input"
          rows={5}
          placeholder={
            // 引导优先级 = 蓝图 > 拍法提问骨架(A3)> 目的档提问骨架 > 通用示例(0824:选了引导就跟着换)。
            // A3:选了拍法用该拍法节拍 {槽位} 组的提问;A4:没选拍法退到目的档 `intakePlaceholder`
            // (按目的定制的提问骨架,与 briefSeed 分工:seed 是种进框里的可编辑正文,这句是空态幽灵字)。
            blueprint
              ? blueprintBriefPlaceholder(blueprint.name)
              : isFreeFlow
                ? // F 批自由创作 = 直发:文案原样发给视频模型(不经编排),所以引导用户写好提示词
                  //(主体+动作+运镜+景别+光线+氛围),再给一条现成范例;整段脚本仍可粘。
                  // ★0914 拍板:自由流只直发,不再提编排出口(那句「想多镜走编排」已跟着功能一起退役)。
                  "直接写给视频模型的提示词,越具体越好:主体 + 动作 + 运镜 + 景别 + 光线 + 氛围。\n例:一杯冰美式放在阳光斜照的木桌上,热气蒸腾,镜头缓缓推近,暖调,浅景深。\n\n也可以整段粘现成脚本,原样直发。"
                : (formulaPlaceholder ??
                  briefPurposeOf(form.briefPurpose)?.intakePlaceholder ??
                  "例如「为超声波清洁机做一段 30 秒抖音带货视频,主体特写 + 抓眼字幕『告别油污 3 秒搞定』」\n\n也可以直接把现成的分镜脚本整段粘进来 —— 系统会忠实拆解,不套拍法。")
          }
          value={form.userInput}
          disabled={deckLocked}
          onChange={(e) => setForm((s) => ({ ...s, userInput: e.target.value }))}
          maxLength={5000}
          // F 批 → 0914:自由流这一框是整段口播/提示词的唯一输入(素材行已撤,别的流不受影响)
          // 加高到 ~260px,装得下一段完整口播;目的流/蓝图态仍是 rows=5 的默认高度。
          className={`resize-none text-sm leading-relaxed${isFreeFlow ? " min-h-[260px]" : ""}`}
        />
        {/* 拍法备料清单(0824):选了拍法就把它节拍里的 {槽位} 亮出来 —— 推荐输入跟着
            拍法走的那一半。只提示不拦截;编排后收声(hasStoryboard 时正文已定)。 */}
        {!hasStoryboard && formulaHint && (
          <p
            data-testid="formula-brief-hint"
            className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-2xs leading-relaxed text-muted-foreground"
          >
            {formulaHint}
          </p>
        )}
        {/* 资料文轻提示(2026-08-10 引导批):400 电话真单实证用户会整段贴业务百科 ——
            只提示不拦截,和「不编排直接出片」的知情确认同一设计哲学。编排后不再唠叨。 */}
        {!hasStoryboard && looksLikeMaterialDoc(form.userInput) && (
          <p
            data-testid="material-doc-hint"
            className="rounded-md border border-amber-300/50 bg-amber-50/60 px-3 py-2 text-2xs leading-relaxed text-amber-800 dark:border-amber-400/30 dark:bg-amber-950/20 dark:text-amber-300"
          >
            {MATERIAL_DOC_HINT}
          </p>
        )}
        {/* F 批 → 0914 收口:自由流素材行整块撤下(直发链路不消费产品图/选品/人物),
            字数计数不能跟着丢 —— 挪到这里自行渲染(原挂在 VideoMaterialRow 尾部)。 */}
        {isFreeFlow && !hasStoryboard && (
          <p
            data-testid="free-input-counter"
            className="text-right text-2xs tabular-nums text-muted-foreground"
          >
            {form.userInput.length} / 5000
          </p>
        )}
        {/* BPWB-1:仿拍档格式行不渲染(时长由原片定、比例跟原片锁死),但被替它定掉的事
            要在决策点说出来 —— 看得见才叫提示。字数计数也落这行(素材行进了①,不再带它)。 */}
        {blueprint && !hasStoryboard && (
          <p
            data-testid="copy-format-facts"
            className="flex flex-wrap items-baseline gap-x-2 text-2xs text-muted-foreground"
          >
            {fidelityMode === "copy" && (
              <span>
                比例 <b className="text-foreground">9:16 竖屏 · 跟原片</b> ·
                目的、拍法、时长由蓝图决定,这一页不用再选
              </span>
            )}
            <span className="ml-auto tabular-nums">{form.userInput.length} / 5000</span>
          </p>
        )}
      </div>
    );

  /**
   * ④ 行动区 —— 幕一唯一的主按钮。
   * ★积分说的是**这颗按钮花的钱**(台本图),不是整条片:整片估算在幕二 cockpit 上,
   *   那里才有真实镜数。编排前镜数未知,报一个"总价"就是编个数字(概览比实际乐观的同型)。
   * ★动作靠右(2026-08-06 用户审阅后调):与右栏 cockpit「生成视频」同侧,
   *   全流程「往右走一步」方向一致;说明文字用 mr-auto 留在左端,不与按钮抢位置。
   *   视觉顺序 = 说明 … [不编排直接出片] [AI 智能编排](order:0/2/3)。
   */
  const intakeAction =
    srcUi.showFreeText || (hasStoryboard && freeShell) ? (
      <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
        {/**
         * 成本 + 省积分开关(2026-08-06 用户审阅 Q2)。
         *
         * ★**不报总价**:编排前镜数真的不可知 —— 镜数由架构师 LLM 定,ai 侧没有确定性的
         *   「时长档 → 镜数」规则(`_DEFAULT_MAX_SHOTS=3` 是单**块**上限,不是全片)。
         *   编个"约 5 镜 = 100 积分"就是凭空造数,与本仓「概览不许比实际乐观/悲观」的口径相反。
         *   能诚实说的只有单价 + 计费规则,以及**怎么少花**。
         * ★省积分开关从分镜网格提到这里:它只在**编排前**有意义(批量出图那一跳读它),
         *   编排完再勾要等下一次重编排才生效 —— 放在成片后的网格里是个迟到的开关。
         */}
        <span className="mr-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-muted-foreground">
          {/* ★编排费(架构师/导演 LLM 开销)与上面「不报总价」那条注不冲突:那条针对的是
              **整片**渲染费(镜数未知,编不出);这一笔与镜数无关(ai 侧 `_video_est` 头注:
              「与片型无关」),故能诚实说出一个区间。取不到价 → `null` → 不渲染这个 span,
              按钮旁退回今天的样子(无这句话),不显示 undefined/占位符。 */}
          {composeEstimateText && (
            <span data-testid="video-compose-estimate">{composeEstimateText}</span>
          )}
          {/* ★ `?? 0` 会印出「台本图每镜 0 积分」—— 拿不到费率时那不是「免费」,是「不知道」,
              而用户会照着「0」去勾这个会真花钱的开关。拿不到就只说规则不说数。 */}
          <span>
            {/* ★ 单价从**价目表** `video.frame` 取(= 首帧结算 SKU;后端按 ai `frame_gen._TIER="2K"`
                报价)。0926 前借 poster.image/openai_2K 报 66、实扣 90;规则文案见 frameRuleCaption。 */}
            {frameRuleCaption(perFrameCredits, { manualTextOnly })}
          </span>
          {/* ★2026-08-09 默认反转:此前默认每镜都出,45s 单 = 15 张 × 700 = 10,500 积分,
              用户原话「不需要出那么多无意义的图」。现在默认只给要当起始画面的镜出图,
              勾选框语义跟着翻面 —— 从「省积分(退出默认)」变成「每镜都出(主动加钱)」。
              判据只认显式 true(见 useVideoCreditEstimate 与 api generateFrames):
              老草稿这个字段是空的,不能读成「他要每镜都出」,否则勾没勾上却烧满图。 */}
          {/* title 挂在 label 上而不是内层 span:挂 span 时鼠标悬在**勾选框本身**没有任何提示,
              只有悬到文字上才出来 —— 0810 走查按「勾选框有无 title」判定,扫 input/label 都是 null。 */}
          <label
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap"
            title="默认只给「会用于出片」的镜出台本图。勾上后每个镜头都出一张,能逐镜看图,但出图积分按镜数翻上去"
          >
            <input
              type="checkbox"
              className="h-3.5 w-3.5 cursor-pointer accent-primary"
              checked={form.previewFrames === true}
              disabled={composing || framingBatch}
              onChange={(e) => setForm((f) => ({ ...f, previewFrames: e.target.checked }))}
              data-testid="video-thrift-frames"
            />
            <span>为每个镜头出台本图(按镜数加钱)</span>
          </label>
        </span>
        <Button
          type="button"
          size="lg"
          className="order-3 h-10 gap-1.5"
          disabled={
            composing ||
            framingBatch ||
            framingSet.size > 0 ||
            // B2 #10(0927 K4 实测):目的卡预填的 99 字模板原文也能点编排 → 误点一次扣 1292 + 台本图 270。
            //   模板原文(未动过的 briefSeed)不算「写了想法」—— 判据与换目的时的 seed 置换同一份。
            isReplaceableBrief(form.userInput) ||
            !!copyRealBlock
          }
          onClick={handleCompose}
          data-testid="video-compose"
        >
          <Wand2 className="h-4 w-4" />
          {composing
            ? "AI 编排中..."
            : framingBatch
              ? // ★不写「并行」(2026-08-06 修):服务端 `VideoDraftOrchestrationService`
                // 四处写明「按镜序**串行**出首帧,后续镜锚定第 0 镜已出帧保跨镜一致」——
                // 界面说并行是**概览比实际乐观**的同型错(用户记得这条)。
                framingBusyLabel(composeFrames)
              : blueprint
                ? fidelityMode === "copy"
                  ? form.explosiveHasRawVideo === true
                    ? "照原片仿拍 · 生成分镜"
                    : "套结构 · 生成分镜"
                  : "按蓝图结构编排"
                : hasStoryboard
                  ? // ★1006 复测:按钮只放动作名。0904 把价钱焊进按钮本体,1440 宽下按钮被截成
                    // 「…最高 1,9…」—— 价钱反而看不全。价钱改由按钮旁的花费小字说(同一行左侧
                    // `video-compose-estimate`,完整句带「按实际用量结算 / 不会超过」),不再重复进按钮。
                    `重新编排(${composeScopeLabel(composeFrames)})`
                  : `AI 智能编排(${composeScopeLabel(composeFrames)})`}
        </Button>
        {/**
         * 「不编排直接出片」—— 次级出口(2026-08-06 重排后补回)。
         *
         * ★这条路要留,但**不能再与主按钮同级**。重排诊断 P2 的病根不是"存在这条路",
         *   而是重排前它是右栏一颗**同样亮的实心按钮 + 带积分数字**,与「AI 智能编排」
         *   同屏竞争 —— 看起来更像最终动作,于是产品只好加弹窗劝返。
         *   实心主按钮 vs 纯文字次级按钮是无法误读的等级差,这才是那个问题的正解。
         * ★仍保留一次确认,但与旧弹窗有本质区别:旧的拦的是"看错版面点错的人"(布局的锅),
         *   现在拦的是**明知故犯者的知情确认**(要花钱、且效果通常差)。
         *   旧弹窗还把坏选择做成了 `AlertDialogAction`(主按钮样式)—— 这次反过来。
         */}
        {!hasStoryboard && (
          <button
            type="button"
            disabled={
              composing ||
              framingBatch ||
              submitting ||
              isReplaceableBrief(form.userInput) ||
              !!copyRealBlock
            }
            onClick={() => setBareSendConfirmOpen(true)}
            data-testid="video-bare-send"
            className="order-2 cursor-pointer rounded text-2xs text-muted-foreground underline decoration-dotted underline-offset-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50"
          >
            {/* V-10:在途要有肉眼可见的反馈 —— 此前提交后这颗按钮毫无变化,用户以为没点上 */}
            {submitting ? "提交中…" : "不编排,直接出片 →"}
          </button>
        )}
        {/* ★把「时长会被钳短」从确认弹窗**前移到按钮旁**(2026-08-13 李文强:「选45秒,视频
            出来变成15s」)。这句话 8-11 就为爱民同类反馈写过,只是躺在确认弹窗第三段 ——
            那个弹窗还同时讲着"会出废片""积分照扣",三条警告挤在一起,最该看的这条被淹了。
            决策发生在**点按钮之前**,提示就得在那儿:看得见才叫提示,点进去才看到叫免责声明。 */}
        {/* B2 #10:框里还是模板原文 → 说清为什么不能点(不然按钮灰着像坏了) */}
        {!hasStoryboard && form.userInput.trim() !== "" && isReplaceableBrief(form.userInput) && (
          <p
            data-testid="video-compose-template-hint"
            className="order-3 text-2xs text-muted-foreground"
          >
            框里还是模板原文 ——
            先写你的想法(产品、卖点、给谁看)再编排,模板原文直接编排会花编排费却拍不出你的产品。
          </p>
        )}
        {!hasStoryboard && bareSendClampsTier(form, bareSendMaxSec(selectedCap?.durationRange)) && (
          <p
            data-testid="video-bare-send-clamp-hint"
            className="order-3 text-2xs text-warning-foreground"
          >
            直发是单镜一次生成、当前模型最长 {bareSendMaxSec(selectedCap?.durationRange)}s ——
            你选的「{form.briefDuration}」得走「AI 智能编排」才出得来。
          </p>
        )}
        {/**
         * ★配音 / 字幕 / 语速**在哪**那句已并入备料台的常驻提示(2026-09-12 四刀 ④):
         *   自由流里行动区与备料台同屏,两条语义重叠的灰字前后脚出现,读起来像重复唠叨。
         *   原文案的两层意思(三样在幕二哪儿调 + 直发本就没有配音字幕)一条不少地
         *   写进了 `VideoIntakeDeck` 那行,testid 也跟着搬过去。
         */}
      </div>
    ) : undefined;

  /**
   * BPWB-1:幕一蓝图态,素材行整体搬进页面级「① 换成你的产品」步骤卡(组件与接线原样,
   * 只挪挂载点)—— 产品替换是仿拍的命根,缩在文本框底下的一行素材位没人看见
   * (EXP-R2V-2 真机实证:不传图 = 每镜各自想象产品外观,段1小熊段2白罐)。
   * 幕二不动:那里素材走分镜工具条,备料台展开时维持旧样。
   */
  const blueprintIntake = !!blueprint && !hasStoryboard;

  /** ① 换成你的产品(蓝图幕一专属)—— 判据与 ProductImagePanel 的 hasProduct 同式。 */
  const hasProductImage = (form.entities ?? []).some(
    (e) => e.source === "image" && e.role === "product" && !!e.key
  );
  /**
   * B3(2026-09-12):「① 换成你的产品」不再是独立一张 primary 卡 —— 作为备料卡的顶端插槽
   * (leadingSlot)与「② 介绍你的产品」文案框同属一张卡(仿照自由流备料台形制),首屏让给操作。
   * 内容(hint + 素材行)一字未动,只脱掉自己的外框,底部一条分隔线与文案框分段。
   */
  const blueprintProductStep = blueprintIntake ? (
    <div
      data-testid="blueprint-product-step"
      className="space-y-2.5 border-b border-border/60 pb-3.5"
    >
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-[13.5px] font-semibold text-foreground">① 换成你的产品</span>
        <span className="text-2xs text-muted-foreground">
          {fidelityMode === "copy" ? "仿拍会把原片里的产品替换成它" : "首帧和分镜会按它画你的产品"}
        </span>
      </div>
      {hasProductImage ? (
        <p
          data-testid="blueprint-product-ok"
          className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-2xs leading-relaxed text-emerald-700 dark:text-emerald-400"
        >
          {fidelityMode === "copy"
            ? "已锁定你的产品外形 —— 每一镜都会按这张图替换原片里的产品。"
            : "产品图已带上 —— 台本图会按图画你的产品。"}
        </p>
      ) : (
        <p
          data-testid="blueprint-product-warn"
          className="rounded-md border border-amber-400/40 bg-amber-500/5 px-3 py-2 text-2xs leading-relaxed text-amber-700 dark:text-amber-400"
        >
          {fidelityMode === "copy"
            ? "还没传产品图 —— AI 只能凭文字想象你的产品长什么样,几个镜头之间外形可能对不上。传一张实拍图,每一镜都按图锁定外形。"
            : "还没传产品图 —— 传一张实拍图,台本图会按图画你的产品,不传就凭文字想象。"}
        </p>
      )}
      <VideoMaterialRow
        form={form}
        setForm={setForm}
        onPickCharacter={(pick) => setForm((s) => applyCharacterPick(s, pick))}
        onClearCharacter={() => setForm((s) => clearCharacterPick(s))}
        disabled={deckLocked}
        castReal={castReal}
        onNavigateToRealCreate={() => navigate("/characters/new-real")}
        draftId={currentDraftId}
      />
      {copyRealBlock && (
        <div
          data-testid="blueprint-copy-real-block"
          className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-2xs leading-relaxed text-destructive"
        >
          <span className="min-w-0 flex-1">
            {copyRealBlock}(选了真人出镜人时照原片仿拍出不了本人,已禁用提交)
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setForm((s) => ({ ...s, explosiveFidelityMode: "learn" }))}
          >
            切「只学套路」
          </Button>
          {!domesticOnly && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => navigate("/ai-video/real")}
            >
              去真人出镜页
            </Button>
          )}
        </div>
      )}
    </div>
  ) : undefined;

  /**
   * 机器层(A5):模型/分辨率/同步音频/任务名 —— 常显卡片行,不再折叠。
   * ★定义在 intakeDeck 之前:幕一它作为 intakeDeck 的 machineSlot(排在画面层与行动区之间);
   *   幕二仍作为 cockpit 里的独立块渲染(见下文 {advanced})。
   */
  const advanced = (
    <VideoAdvancedSection
      caps={caps}
      form={form}
      setForm={setForm}
      effectiveModel={effectiveModel}
      effectiveMode={effectiveMode}
      selectedCap={selectedCap}
      setModelProvider={setModelProvider}
    />
  );

  /**
   * D 批:目的流两栏 / 自由创作右栏里的机器层 —— dense(单列堆叠,适配 ~380px 右栏);
   * 模型块已在 VideoModelSelect 里收成一行(点开才展开选型)。
   */
  const advancedDense = (
    <VideoAdvancedSection
      caps={caps}
      form={form}
      setForm={setForm}
      effectiveModel={effectiveModel}
      effectiveMode={effectiveMode}
      selectedCap={selectedCap}
      setModelProvider={setModelProvider}
      dense
    />
  );

  // A1:有「时长×拍法 联动卡」的流(自由外壳选定目的、幕一)—— 时长在联动卡里,画面层不再重复渲。
  const shootPlanShown = !!lockedPurpose && !hasStoryboard;

  /** 备料台(两幕共用同一个组件,幕二里由摘要条控制展开)。 */
  const intakeDeck = (
    <VideoIntakeDeck
      form={form}
      setForm={setForm}
      disabled={deckLocked}
      draftId={currentDraftId}
      autoOpenProductLibrary={productEntry}
      onProductLibraryAutoOpened={() => setProductEntry(false)}
      onPurposeChange={applyPurpose}
      onPickCharacter={(pick) => setForm((s) => applyCharacterPick(s, pick))}
      onClearCharacter={() => setForm((s) => clearCharacterPick(s))}
      castReal={castReal}
      onNavigateToRealCreate={() => navigate("/characters/new-real")}
      // A2 目的锁定:选定目的后目的行退场(横幅接管),只在「未选目的」的自由流出现。
      showPurpose={srcUi.showPurpose && !lockedPurpose}
      showFormat={srcUi.showFormat}
      // A4:有联动卡时时长在联动卡里(别一字段两入口);无联动卡的流(蓝图学套路/幕二/未选目的)就地渲。
      showDurationInFormat={!shootPlanShown}
      // F 批:目的流把比例挪进左栏时长卡那一行 → 画面层不再渲比例(否则一字段两入口);其余流(蓝图/幕二/单列)仍在画面层就地渲。
      showRatioInFormat={!shootPlanShown}
      // A5/D:机器层排在画面层与行动区之间;但目的流两栏把它挪进右栏(machineSlot 关掉),幕二在 cockpit 独立渲染。
      machineSlot={hasStoryboard || isPurposeFlow ? undefined : advanced}
      showMaterial={freeShell && !blueprintIntake}
      // B3:蓝图态「① 换成你的产品」压进本卡顶端,与文案框合成一张备料卡。
      leadingSlot={blueprintProductStep}
      // 蓝图幕一:素材行(计数随行)进了①,计数改由 copy-format-facts 行携带
      counter={
        srcUi.showFreeText && !blueprintIntake ? `${form.userInput.length} / 5000` : undefined
      }
      // D:目的流两栏把 CTA(编排/直发/报价)挪进右栏 → 备料台不再带行动区。
      action={isPurposeFlow ? undefined : intakeAction}
    >
      {intakeContent}
    </VideoIntakeDeck>
  );

  /**
   * A1「时长×拍法 联动卡」—— 幕一、自由外壳、已选定有效目的时显示(蓝图/海报/模板的目的另有来路,
   * 不出;A7③:蓝图态本就无 briefPurpose → lockedPurpose 为 null → 联动卡不出 → 无拍法选择器)。
   */
  // D:两栏布局把时长拆成左栏独立卡(showDuration=false)、合身度搬进右栏摘要面板(showFit=false)。
  const shootPlanCard = shootPlanShown ? (
    <VideoShootPlanCard
      form={form}
      setForm={setForm}
      purpose={lockedPurpose!}
      disabled={deckLocked}
      showDuration={false}
      showFit={false}
    />
  ) : null;

  /** D/F:目的流左栏第一行 —— 时长 + 比例 同一张卡(时长档独占一行浪费一半宽 → 比例挪来并排,
      窄屏纵向堆回落,不横向溢出)。比例走 VideoRatioChips(与画面层同一份 lockedFields 写入)。 */
  const durationCard = isPurposeFlow ? (
    <section className="rounded-[calc(var(--radius)*1.15)] border border-border/70 bg-card/60 px-5 py-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:gap-x-8">
        <div className="min-w-0">
          <VideoDurationTierRow form={form} setForm={setForm} disabled={deckLocked} />
        </div>
        <div className="min-w-0">
          <VideoRatioChips form={form} setForm={setForm} disabled={deckLocked} />
        </div>
        {/* 0924 车 C:调性三档(带货喊话/品牌克制/高奢静默),默认跟租户行业,点过即用户说了算。 */}
        <div className="min-w-0">
          <VideoToneRegisterChips
            form={form}
            setForm={setForm}
            value={toneRegisterNow}
            disabled={deckLocked}
          />
        </div>
      </div>
    </section>
  ) : null;

  /** D:目的流 / 自由创作右栏「这条片」摘要面板(9:16 出片形状 + 摘要列 + 合身度信号)。 */
  const planSummaryPanel =
    isPurposeFlow || isFreeFlow ? (
      <VideoPlanSummaryPanel
        form={form}
        setForm={setForm}
        purpose={isPurposeFlow ? lockedPurpose : null}
        pickedFormulaName={isPurposeFlow ? form.pickedFormulaName : null}
        disabled={deckLocked}
      />
    ) : null;

  const composeBusy = composing || framingBatch || framingSet.size > 0;
  // B2 #10:模板原文不算输入(与上面编排按钮同一判据)
  const noInput = isReplaceableBrief(form.userInput);

  /**
   * D:目的流右栏 CTA —— 编排优先。报价(编排费 + 台本图口径)+ 「AI 智能编排」主按钮 +
   * 「不编排直接出片」次链接(走既有 bareSend 确认弹窗)。逻辑/文案与旧 intakeAction 同源,
   * 只是竖排进右栏(旧 intakeAction 仍供蓝图/海报/模板单列流用)。
   */
  const purposeCta = isPurposeFlow ? (
    <div className="space-y-2 rounded-lg border bg-card p-3">
      <div className="space-y-1 text-2xs text-muted-foreground">
        <div>{frameRuleCaption(perFrameCredits, { manualTextOnly })}</div>
        <label
          className="flex cursor-pointer items-center gap-1.5"
          title="默认只给「会用于出片」的镜出台本图。勾上后每个镜头都出一张,能逐镜看图,但出图积分按镜数翻上去"
        >
          <input
            type="checkbox"
            className="h-3.5 w-3.5 cursor-pointer accent-primary"
            checked={form.previewFrames === true}
            disabled={composing || framingBatch}
            onChange={(e) => setForm((f) => ({ ...f, previewFrames: e.target.checked }))}
            data-testid="video-thrift-frames"
          />
          <span>为每个镜头出台本图(按镜数加钱)</span>
        </label>
      </div>
      <Button
        type="button"
        className="w-full gap-1.5"
        disabled={composeBusy || noInput}
        onClick={handleCompose}
        data-testid="video-compose"
      >
        <Wand2 className="h-4 w-4" />
        {composing
          ? "AI 编排中..."
          : framingBatch
            ? framingBusyLabel(composeFrames)
            : `AI 智能编排(${composeScopeLabel(composeFrames)})`}
      </Button>
      {/* ★1006 复测:价钱从按钮本体挪到按钮正下方小字(右栏窄,焊进按钮会被截断) */}
      {composeEstimateText && (
        <div
          className="text-center text-2xs text-muted-foreground"
          data-testid="video-compose-estimate"
        >
          {composeEstimateText}
        </div>
      )}
      {/**
       * ★分流收口(2026-09-14 拍板):目的流**只编排**——「不编排直接出片」功能出口整个退役
       * (随之退役的还有 BareSendConfirmDialog 在目的流的唯一触发点与钳短提示;蓝图/海报/
       * 模板单列流的直发不在此列,拍板明确不动)。留的是**认知出口**:走错路的用户一句话
       * 被指去自由创作,而不是在本页再做一次「编排还是直发」的选择 —— 这个选择在视频舱
       * 选入口时已经做完了。
       */}
      <button
        type="button"
        onClick={() => navigate("/ai-video/create")}
        data-testid="video-purpose-goto-free"
        className="block w-full cursor-pointer rounded text-center text-2xs text-muted-foreground underline decoration-dotted underline-offset-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        只想让文字直接出一段画面?去自由创作 →
      </button>
    </div>
  ) : null;

  /**
   * D:自由创作右栏 CTA —— 只直发。报价(直发单段秒数 × 单价,可确定)+「直接出片」主按钮
   * (直连 onSubmit,不套编排劝返弹窗:自由流直发就是本意)+「回视频舱选个目的」认知出口
   * (下方 0914 分流收口注释)—— 不再有「让 AI 编成多镜短片」次链接,那个功能出口已退役。
   */
  const freeCta = isFreeFlow ? (
    <div className="space-y-2 rounded-lg border bg-card p-3">
      <div className="text-2xs text-muted-foreground">
        {quotedBareSend != null ? (
          <>
            预计扣 <span className="font-medium text-foreground">{quotedBareSend}</span> 积分 ·
            {bareSecs}s 直发
          </>
        ) : (
          "积分按实际出片时长与分辨率结算"
        )}
      </div>
      {autoUpgrade && (
        <div
          className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-2xs leading-relaxed text-amber-700 dark:text-amber-300"
          data-testid="video-free-auto-upgrade"
        >
          已自动改用「{autoUpgrade.toLabel}」:{autoUpgrade.reason}。
          {quotedBaselineSend != null && quotedBareSend != null && (
            <>
              {" "}
              价格随档变化:{autoUpgrade.fromLabel} {Math.min(bareSecs, autoUpgrade.from.maxSec)}{" "}
              秒约 {quotedBaselineSend.toLocaleString()} 积分 → 现在 {bareSecs} 秒约{" "}
              {quotedBareSend.toLocaleString()} 积分。
            </>
          )}{" "}
          想省积分可把时长改短,或在「模型 → 手动指定」里自选。
        </div>
      )}
      <Button
        type="button"
        className="w-full gap-1.5"
        disabled={composeBusy || submitting || bootstrapping || noInput}
        onClick={() => void onSubmit()}
        data-testid="video-free-direct-send"
      >
        <Film className="h-4 w-4" />
        {submitting ? "提交中…" : "直接出片"}
      </Button>
      {/**
       * ★分流收口(2026-09-14 拍板):自由流**只直发**——「让 AI 编成多镜短片」功能出口退役。
       * 自由创作=一段文字直接生一段画面;要成片(多镜/口播/拍法)= 回视频舱选个目的。
       * 粘完整脚本的用户同样走目的流(SCRIPT 忠实拆解那条路在目的流里原生存在)。
       */}
      <button
        type="button"
        onClick={() => navigate("/ai-video")}
        data-testid="video-free-goto-purpose"
        className="block w-full cursor-pointer rounded text-center text-2xs text-muted-foreground underline decoration-dotted underline-offset-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        要出完整多镜短片?回视频舱选个目的 →
      </button>
    </div>
  ) : null;

  /**
   * D:自由创作左栏输入体(独立精简页,不穿目的壳)——
   * 0914 老板拍板两处收口:
   *   ① 素材行(产品图/产品库选品/人物)整块撤下 —— 直发单镜的 ai 侧只读 userInput +
   *      风格/主题/品牌焦点 + firstFrameUrl,三件素材在这条链路一个都不被消费,留着是假选项。
   *      字数计数不能跟着丢,改由「想拍什么」框自己右下角渲染(见 intakeContent 自由态)。
   *   ② 顺序对齐目的流:时长+比例(先定形状)放最上 → 想拍什么大框 → 风格条收尾
   *      (原先时长排在想拍什么下面)。
   */
  const freeCreateBody = isFreeFlow ? (
    <div
      data-testid="video-free-create-body"
      className="space-y-3.5 rounded-[calc(var(--radius)*1.15)] border-[1.5px] border-primary/40 bg-card/60 px-5 py-4"
    >
      {/* 时长(单段秒数)+ 比例 并排一行(时长独占一行浪费一半宽);窄屏纵向堆回落。 */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:gap-x-8">
        <div className="min-w-0">
          <VideoClipDurationRow
            form={form}
            setForm={setForm}
            disabled={deckLocked}
            fastMaxSec={15}
            autoModelLabel={selectedCapLabel}
          />
        </div>
        <div className="min-w-0">
          {/* 上传了首帧:画幅跟图走(ratio 发 adaptive),比例 chips 置灰免得「选了不生效」。 */}
          <VideoRatioChips
            form={form}
            setForm={setForm}
            disabled={deckLocked || (form.freeMaterialMode === "frame" && !!form.firstFrameKey)}
          />
          {form.freeMaterialMode === "frame" && !!form.firstFrameKey && (
            <p className="mt-1 text-2xs text-muted-foreground">已传首帧,画幅跟图走</p>
          )}
        </div>
      </div>
      {intakeContent}
      {/* 0924 车 B:素材行回归(只放开图片):不用 / 上传首帧(可再传尾帧)/ 参考图 ≤4,互斥。
          0914 撤掉的是「ai 不消费的假选项」;这次三种都有真实消费面(契约 §3.3)。 */}
      <FreeMaterialRow form={form} setForm={setForm} disabled={deckLocked} />
      {/* 画面层只留风格(比例已并进上面的时长行)。 */}
      <VideoFormatRow
        form={form}
        setForm={setForm}
        disabled={deckLocked}
        showDuration={false}
        showRatio={false}
      />
    </div>
  ) : null;

  /**
   * B2(2026-09-12):「原片拆解」—— 把「这次带给 AI 的原文」(HandoffPreview)+「参考片在拍什么」
   * (ReferenceContentPanel)合成**一条默认折叠**,展开才显全文。首屏让给操作(备料卡),
   * 查证信息收进这一条。★不动 payload 解析与 compose,只动布局与折叠。
   */
  const handoffLineCount = blueprint?.payload
    ? buildPromptPreview(blueprint.payload).split("\n").filter(Boolean).length
    : 0;
  const refBeatCount = (form.explosiveReferenceContent ?? "")
    .split("\n")
    .filter((l) => /^\d+(\.\d+)?-\d/.test(l.trim())).length;
  const originalCutMeta = [
    handoffLineCount > 0 ? `${handoffLineCount} 条原文` : null,
    refBeatCount > 0 ? `逐拍 ${refBeatCount} 拍` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const blueprintOriginalCut =
    blueprint && (handoffLineCount > 0 || form.explosiveReferenceContent != null) ? (
      <Collapsible open={originalCutOpen} onOpenChange={setOriginalCutOpen}>
        <div className="rounded-lg border border-border/60 bg-card/40">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              data-testid="blueprint-original-cut-toggle"
              className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm"
            >
              <Info className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="font-medium text-foreground">原片拆解</span>
              {originalCutMeta && (
                <span className="text-2xs font-normal text-muted-foreground">
                  {originalCutMeta}
                </span>
              )}
              <span className="ml-auto text-2xs text-primary">
                {originalCutOpen ? "收起" : "展开"}
              </span>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                  originalCutOpen ? "rotate-180" : ""
                }`}
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-2 border-t border-border/60 px-3.5 py-3">
            {blueprint.payload && <HandoffPreview payload={blueprint.payload} />}
            {form.explosiveReferenceContent != null && (
              <ReferenceContentPanel
                content={form.explosiveReferenceContent}
                original={referenceContentOriginalRef.current ?? form.explosiveReferenceContent}
                disabled={deckLocked}
                onChange={(next) => setForm((s) => ({ ...s, explosiveReferenceContent: next }))}
              />
            )}
          </CollapsibleContent>
        </div>
      </Collapsible>
    ) : null;

  /** 服务端 Job 状态条:编排 + 出图全在后端跑,切走/刷新/关页不丢;回来 ?draftId 续轮询。 */
  const jobBar =
    framingBatch ||
    frameGenStatus === "PARTIAL" ||
    frameGenStatus === "FAILED" ||
    frameGenStatus === "COMPOSE_FAILED" ? (
      <div
        className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs ${
          framingBatch
            ? "border-primary/30 bg-primary/5 text-foreground"
            : "border-amber-300 bg-amber-50 text-amber-800"
        }`}
      >
        {framingBatch && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {/**
         * ★别再写「约 1-2 分钟」(2026-08-14 四天反馈校对 TOP#6)。
         *
         * 四人反复提「太慢」。原条目拿 940 秒来对比,我上一版判它「940 是整个出片任务、
         * 1-2 分钟说的是编排那一步,两码事」并撤回 —— **撤回是对的,但结论下早了**:
         * 本轮把编排那一步**单独量了一次**(关掉 AI_VIDEO_MOCK 真跑),数字比 940 还难看:
         *
         *   ai 侧 `POST /api/v1/video/llm/compose` 实测 **772 秒**才返回 200。
         *   为什么这么久 —— 模型链是**串行降级**,每挂一个要等满超时(240s)再换下一个:
         *     gpt-5.5 → 400 → deepseek-v4-pro → 空响应 → qwen-plus → 200 ✓
         *   而 backend 的 socket timeout 是 **510s**(见 application.yml:191 那条注释),
         *   于是 511s 就判了 COMPOSE_FAILED —— **ai 在 772s 时其实成功了,那份编排被丢掉**。
         *
         * 所以这里不写平均值(本机 gpt-5.5 走中转返 400,未必代表线上),改写**系统自己的
         * 确定上界**:超过 510s 必然失败,这是代码里写死的,是一句可证实的承诺。
         * ★真正该修的是那条串行降级链和「成功结果被超时丢弃」,那要动 ai + backend,不在这一刀里。
         */}
        {frameGenStatus === "COMPOSING"
          ? "AI 编排中…(架构师立意 → 导演分镜 → 审片人过卷;顺利 2-4 分钟,最长 8 分钟)"
          : framingBatch
            ? `台本图生成中… ${shots.filter((sh) => sh.firstFrameKey).length}/${shots.length}(切走 / 刷新不影响 —— 回到这份草稿自动续看)`
            : frameGenStatus === "COMPOSE_FAILED"
              ? // VID-REFUND-1:原文案写死「未产生出图扣费」—— 出图确实没跑,但**编排费是另一笔**,
                // 0818 账目实锤失败任务里 15 条真跑了编排并被扣费(合计 10,829)。措辞改由
                // composeFailKind 决定:只有代码层能保证没 settle 的那两种才说"未扣",
                // 没等到 ai 回包时如实说"可能已扣"并指向账单(见 composeFailNotice)。
                composeFailedNotice(composeFailKind)
              : frameGenStatus === "PARTIAL"
                ? // ★全败≠部分失败(2026-08-10):0/N 成功几乎必是渠道级故障(渠道 key 失效实测:
                  // 7 镜 10 秒内全败)—— 这时让用户逐镜点 7 次「重新生成」等于逐镜再撞 7 次墙。
                  // ★原「失败不扣费」是反着说的:PARTIAL 恰恰扣了钱(0818 实测 5 条合计 38,004),
                  // 已成功的镜是真计费的。改成陈述真实镜数,并说清重出要再花钱。
                  frameFailedNotice(
                    shots.filter((sh) => sh.previewKey || sh.firstFrameKey).length,
                    shots.length
                  )
                : frameFailedNotice(
                    shots.filter((sh) => sh.previewKey || sh.firstFrameKey).length,
                    shots.length
                  )}
        {(frameGenStatus === "PARTIAL" || frameGenStatus === "FAILED") &&
          !framingBatch &&
          shots.some((sh) => !sh.previewKey && !sh.firstFrameKey) && (
            <button
              type="button"
              onClick={() => void regenerateAllMissingFrames()}
              disabled={framingSet.size > 0}
              data-testid="video-regen-all-frames"
              className="ml-auto shrink-0 cursor-pointer rounded-md border border-amber-400/60 px-2 py-1 text-2xs font-semibold text-amber-700 transition-colors hover:bg-amber-100/60 disabled:pointer-events-none disabled:opacity-50 dark:text-amber-400 dark:hover:bg-amber-950/40"
            >
              {framingSet.size > 0 ? "重出中…" : "重出全部缺图的镜"}
            </button>
          )}
      </div>
    ) : null;

  const errorBar = submitError ? (
    <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
      {submitError}
    </div>
  ) : null;

  return (
    /**
     * ★容器宽度(2026-08-10 满屏批):两幕统一 w-full 铺满。
     * 历史:08-06 曾按幕给定宽(幕一 1100 / 幕二 1700),当时的错位根因是**容器与子元素不同宽**
     * (容器 1700、备料台 1100 → 来源条/页头/备料台三者对不齐),不是「太宽」;统一满宽后
     * 子元素同宽对齐,错位不会复现。再前身恒 max-w-7xl(1280),1920 下两侧空转 640px,
     * 用户 08-10 点名「新建页没充满」。
     */
    <>
      {/* VID-OVL-3:悬浮球球道(与成片详情页同口径,见那边注释)。xl 起留 56px,窄屏不动。 */}
      <div className="w-full xl:pr-14">
        {/* ── 页头:页名 + 步骤指示器 + 草稿/取消 ── */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold">新建视频</h1>
            <div className="mt-1.5">
              {/* 0914:自由创作没有「分镜」这一幕,步骤条按 isFreeFlow 分叉成两步(蓝图/海报/目的流不动)。 */}
              <VideoStepIndicator
                current={videoStepOf(hasStoryboard, submitting)}
                free={isFreeFlow}
              />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* 手动保存草稿(自由创作流;有 storyboard 或文案才可点)。编排后也会自动存。 */}
            {srcUi.showSaveDraft && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={
                  savingDraft ||
                  framingBatch || // 出图/编排中服务端是 SoT 唯一写者,手动存会 dual-write 冲突
                  (!form.userInput.trim() && !hasStoryboard)
                }
                onClick={() => void saveDraft(false)}
              >
                {savingDraft ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
                {currentDraftId ? "更新草稿" : "保存草稿"}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/ai-video")}
              disabled={submitting}
              className="gap-1.5 text-muted-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              返回视频舱
            </Button>
          </div>
        </div>

        {/* VID-UX-1:在途编排恢复入口 —— 白纸进新建页而后台还有一份在编排/出图的草稿时,
            把「回来自动续看」的承诺兑现在这一页(不用先想起去草稿箱)。 */}
        {resumableDraft && !resumeDismissed && !currentDraftId && (
          <div
            data-testid="resume-compose-banner"
            className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5 text-sm"
          >
            <span className="min-w-0">
              上次的 AI 编排还在后台进行:
              <span className="font-medium">『{resumableDraft.name}』</span>
            </span>
            <Button
              size="sm"
              className="cursor-pointer"
              onClick={() => {
                // 走与 ?draftId= 深链完全同一条恢复路(拉草稿→重签→灌回→续轮询),
                // 整页装载换取零第二份恢复逻辑 —— 恢复路只有一条,不复制判据。
                window.location.assign(`/ai-video/create?draftId=${resumableDraft.draftId}`);
              }}
            >
              继续上次的编排
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="cursor-pointer text-muted-foreground"
              onClick={() => setResumeDismissed(true)}
            >
              先不用,新建一条
            </Button>
          </div>
        )}

        {/**
         * ★顶栏:A2 目的锁定后,自由流选定目的 → 幕一出「拍摄任务书」横幅(替换原「视频目的」
         *   来源条 + 备料台换目的区);横幅只展示、不给换,换目的的唯一出口是右侧「回视频舱重选」。
         *
         * 其余来源仍走来源条(五来源唯一挂载点,只回答「我从哪来」):
         * 幕二只对**带外部出处**的来源渲染(模板/海报/追爆蓝图);自由 / 目的两档在幕二不渲染
         *   (摘要条已逐字说了目的 + 拍法 + 比例 + 风格,再来一条 = 两条 bar 说同一句话)——
         * 例外是 AI 分歧留痕(VID-CMP-06,notice.detail 非空)时幕二也渲,让分歧行编排后不消失。
         */}
        {lockedPurpose && !hasStoryboard ? (
          <div className="mt-4">
            {/* A2:横幅只留目的药丸+副题+回舱重选;拍法与叙事弧节拍带已下移进「时长×拍法 联动卡」。 */}
            <VideoPurposeBanner
              purpose={lockedPurpose}
              onBackToHub={backToHub}
              disabled={deckLocked}
            />
          </div>
        ) : (
          (!hasStoryboard ||
            srcUi.kind === "template" ||
            srcUi.kind === "poster" ||
            srcUi.kind === "explosive" ||
            Boolean(purposeNotice(form).detail)) && (
            <div className="mt-4">
              <VideoSourceBar data={sourceBarData} disabled={deckLocked} />
            </div>
          )
        )}

        {/* 2026-09-12 B2:「参考片在拍什么」+「带给 AI 的原文」已收进蓝图幕一/幕二的「原片拆解」
            折叠(blueprintOriginalCut),不再在这里各占一条首屏。 */}

        {/* From-poster 上下文(条件显) */}
        {fromPoster && stripSystemPromptLines(posterPrompt) && (
          <Collapsible open={posterPromptOpen} onOpenChange={setPosterPromptOpen} className="mt-3">
            <div className="rounded-lg border bg-muted/30 p-3">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between text-left text-sm"
                >
                  <span className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-primary" />
                    海报原提示词(只读,供参考)
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${posterPromptOpen ? "rotate-180" : ""}`}
                  />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <pre className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded border bg-background/50 p-3 text-xs text-muted-foreground">
                  {stripSystemPromptLines(posterPrompt)}
                </pre>
              </CollapsibleContent>
            </div>
          </Collapsible>
        )}

        {hasStoryboard ? (
          /* ══════════ 幕二 · 分镜工作台 ══════════ */
          <div className="mt-4 space-y-4">
            <VideoIntakeSummaryBar
              form={form}
              expanded={deckOpen}
              onToggle={() => setDeckOpen((v) => !v)}
              stale={stale}
              onRecompose={handleCompose}
              busy={deckLocked}
            />
            {deckVisible && intakeDeck}
            {/* B2:蓝图态「原片拆解」折叠(带给AI原文 + 参考片在拍什么)—— 幕二也留一份查证入口。 */}
            {blueprintOriginalCut}
            {jobBar}

            {/**
             * 导演交代卡(W4,2026-09-13)—— 取代原「故事大纲」折叠卡:架构师产的 logline/
             * 主角/节拍收进本卡「完整大纲」折叠里,新增导演的选料/论证弧/审片摘要。
             * 「换进去」确认后复用摘要条同一条重编排入口(guardedCompose → triggerComposeJob),
             * 不新造编排通道;这里只把「新选集」以 pinnedFactIds 塞进 baseForm 直接调用,
             * 避开 setForm 异步 + 立刻读闭包 form 的过时态坑(setForm 还没生效前 form 是旧值)。
             */}
            <VideoDirectorBriefCard
              form={form}
              shotCount={shots.length}
              disabled={deckLocked}
              onRecomposeWithPins={(pinnedFactIds) =>
                guardedCompose(() => triggerComposeJob({ ...form, pinnedFactIds }))
              }
              onClearPinnedFacts={() => setForm((s) => ({ ...s, pinnedFactIds: null }))}
            />

            {/**
             * ★窄屏把 cockpit 提到分镜网格**上面**(2026-08-06 走查实测补):
             * 桌面是「左分镜 + 右常驻」,但 <1024 时右栏塌到文档流末尾 —— 实测 768 下
             * 「生成视频」落在 docTop 1902 而页高 1979,390 下更是 2829/2898:
             * 出片按钮被埋在整条分镜脚本之后,要一路滚到底才够得着。
             * 桌面顺序靠 lg:order-none 还原,DOM 顺序保持「分镜在前」(读屏与 tab 序仍是内容优先)。
             */}
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
              {/* testid 给版面类走查当稳锚(VID-ACT-08 曾因拿文案定位左列而假失败) */}
              <div
                data-testid="storyboard-column"
                className="order-2 min-w-0 flex-1 space-y-4 lg:order-none"
              >
                {/**
                 * 「恢复上一版分镜」(批⑫a)—— 只在快照槽有东西时出现。
                 *
                 * ★放在分镜区顶部而不是收进某个菜单:撤销键**必须看得见**,
                 *   藏起来等于没有(没人会去找一个自己不知道存在的撤销)。
                 * ★同时它不该抢眼 —— 常用动作是改分镜、出片,不是来回切版本,
                 *   所以是一颗虚线下划线的文字按钮,与「不编排,直接出片」同一等级。
                 * ★编排/出图进行中禁用:那时服务端正在写这些字段,前端再写就是 dual-write。
                 */}
                {storyboardBackup && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      disabled={deckLocked}
                      onClick={() => void handleRestoreStoryboard()}
                      data-testid="video-storyboard-restore"
                      title="切回重新编排之前的那一版分镜(含逐镜口播、时长与台本图);再点一次可切回来"
                      className="inline-flex cursor-pointer items-center gap-1 rounded text-2xs text-muted-foreground underline decoration-dotted underline-offset-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50"
                    >
                      <Undo2 className="h-3 w-3" />
                      恢复上一版分镜
                    </button>
                  </div>
                )}
                {/* 分镜工具条:出场主体(文本锁编辑)+ 素材位(唯一改图入口)+ 总长/超密摘要。 */}
                <StoryboardToolbar
                  subjects={form.subjects}
                  onSubjectsChange={(next) => setForm((s) => ({ ...s, subjects: next }))}
                  shots={shots}
                  disabled={framingBatch}
                  productSlot={
                    freeShell ? (
                      <ProductImagePanel
                        draftId={currentDraftId}
                        entities={form.entities ?? []}
                        onChange={(entities, productRefKey) =>
                          setForm((s) => ({ ...s, entities, productRefKey }))
                        }
                      />
                    ) : undefined
                  }
                />
                {/**
                 * ★时间轴带(2026-08-06 幕二重设计):网格里 4s 的镜和 15s 的镜长得一样大,
                 * 而**节奏**恰恰是成片好坏的关键。这条带按秒等比,并且把「段」画出来 ——
                 * 发给模型的 prompt 是按段的(相邻镜合 1~3 镜一段),此前用户完全看不到这个结构。
                 * 段来自 compilePreview 的真回包,不是前端猜的。
                 */}
                {/**
                 * 黄金 3 秒(2026-08-10 导演台本上屏):hookPlan 是导演产的开场设计
                 * (文案/画面/音效三轨),此前只默默存 formJson。开场决定完播率,
                 * 编排一回来就该让用户先审这一秒 —— 文字层先行,不等台本图。
                 */}
                {hookCopy && (
                  <div
                    data-testid="video-hook-plan"
                    className="flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-lg border border-amber-300/50 bg-amber-50/60 px-3 py-2 text-xs dark:border-amber-400/30 dark:bg-amber-950/20"
                  >
                    <span className="shrink-0 font-semibold text-amber-700 dark:text-amber-400">
                      黄金 3 秒
                    </span>
                    <span className="text-foreground/85">{hookCopy}</span>
                    {hookVisual && <span className="text-muted-foreground">画面:{hookVisual}</span>}
                  </div>
                )}
                <VideoTimelineBand
                  shots={shots}
                  segments={compilePreview.segments}
                  selected={selectedShot}
                  onSelect={setSelectedShot}
                  beatLabels={storyBeatLabels}
                  narrativeRoles={form.narrativeRoles ?? undefined}
                />
                {/**
                 * ★矮卡分镜条:实测老卡单张 769px(一张卡快占满一屏),里面 6 个镜头语言选择器
                 * 绝大多数时候是「AI 定」不动却占着固定版面。现在左栏 = 导航(看全片+选一镜),
                 * 右栏 = 工作台(改这一镜)。
                 */}
                <VideoShotStrip
                  shots={shots}
                  ratio={form.ratio}
                  selected={selectedShot}
                  onSelect={setSelectedShot}
                  onAddShot={addShot}
                  previewFrames={form.previewFrames}
                  disabled={framingBatch}
                  beatLabels={storyBeatLabels}
                  beatHighlights={
                    blueprint && hoveredBeat != null
                      ? shotBeatMap.map((bi) => bi === hoveredBeat)
                      : undefined
                  }
                  onBeatHover={(i, hover) =>
                    setHoveredBeat(hover ? (shotBeatMap[i] ?? null) : null)
                  }
                  factTexts={form.factTexts}
                />

                {/* ★声音与字幕 / BGM 只在幕二出现(2026-08-06 重排 P4):它们在 ai 侧
                  `ComposeRequest` 里**根本没有** —— 是编排完由 AI 预填、出片时才消费的
                  出片期参数。重排前它无条件常驻、且排在编排按钮下方与风格/拍法平级,
                  看起来像"编排前要填的"。编排完再调,正是它们该待的地方。 */}
                <VoiceSubtitlePanel
                  value={{
                    voiceTone: form.voiceTone,
                    voiceSpeechRate: form.voiceSpeechRate,
                    subtitleFontScale: form.subtitleFontScale,
                  }}
                  onChange={(patch) =>
                    setForm((s) => ({
                      ...s,
                      ...patch,
                      lockedFields: withUserLocks(s.lockedFields, patch),
                    }))
                  }
                  bgmSlot={
                    <VideoBgmPanel
                      bgms={bgms}
                      bgmTab={bgmTab}
                      setBgmTab={setBgmTab}
                      form={form}
                      setForm={setForm}
                      customBgmFile={customBgmFile}
                      customBgmUrl={customBgmUrl}
                      uploadingBgm={uploadingBgm}
                      onClearCustomBgm={() => {
                        setCustomBgmFile(null);
                        setCustomBgmUrl(null);
                      }}
                      onCustomBgmUpload={handleCustomBgmUpload}
                    />
                  }
                />

                {advanced}

                {/* W3 动作参考 × 引擎提示:默认自动升档(ai 侧 route_chunk_model 升档腿),
                  1005 核实:首次出片按报价单兑现、不随升档加价,蓝条如实说「照报价扣」。
                  例外=带首帧参考时 ai 守门不升(first_frame×reference 禁混)。 */}
                {effectiveModel.provider !== "seedance" &&
                  willUpgradeSomeChunk(form) &&
                  ((form.firstFrameUrl ?? "").trim() ? (
                    <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm text-amber-700 dark:text-amber-400">
                      已选人物/动作参考,但任务带首帧参考时不会自动升级引擎(首帧与参考素材互斥)
                      ——参考会被忽略。去掉首帧,或在「高级选项 → 手动指定」切到「综合」档。
                    </div>
                  ) : (
                    <div className="rounded-md border border-sky-500/40 bg-sky-500/5 p-3 text-sm text-sky-700 dark:text-sky-400">
                      {upgradeNoticeSubject(form, Object.keys(autoMotion).length)}
                      :这些段落出片时会自动换用「综合」档引擎(跨镜同人/产品外形锁/动作迁移/口型对齐只有它接得住),其余段落仍走当前{" "}
                      {videoProviderLabel(effectiveModel.provider, effectiveModel.model)};
                      {UPGRADE_BILLING_NOTE}。
                    </div>
                  ))}

                {/* 多镜动作 × 段级配额诚实提示:相邻镜合并成段,每段只有第一条动作生效。 */}
                {(form.references ?? []).filter((refs) =>
                  (refs ?? []).some((r) => r.role === "motion")
                ).length >= 2 && (
                  <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm text-amber-700 dark:text-amber-400">
                    选了多镜动作参考:出片时相邻镜会合并成段,每段只有第一条动作生效,其余会被
                    裁掉(引擎参考时长上限)。建议只在最关键的镜挂动作。
                  </div>
                )}

                {errorBar}
              </div>

              {/* 右侧常驻工作台:结构轨(蓝图路)+ 实时预览 + 设置摘要 + 积分 + 生成。
                ★它只属于幕二 —— 编排前它显示的全是备料台控件的复读、预览框自己写着
                「AI 编排后显示」,而那颗「生成视频」还与编排按钮同屏竞争(见 bareSend 注释)。 */}
              {/* ★右栏加宽 320 → 380(2026-08-06):它现在是**工作台**而不是预览框 ——
                  要装下真 prompt 正文、6 个镜头语言选择器、参考缩略图带。320 下 prompt
                  每行只剩十几个字,读起来像竖排。 */}
              {/* VID-OVL-1(2026-08-18 老板报障「往下滑右边展示页跟按钮重叠」):此前 sticky 钉在
                  VideoShotInspector 内层卡上,同栏下方的估价/「生成视频」条不随钉 → 滚动时钻到
                  卡底下被盖掉。改整条右栏一个 sticky 单元(检视器+出片条同进同退,「任何时候都
                  够得着」这句才真成立);栏比视口高时栏内自滚,出片条靠栏内滚动始终可达。 */}
              <div className="order-1 space-y-3 lg:order-none lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:w-[380px] lg:shrink-0 lg:overflow-y-auto">
                {blueprint && (
                  <BlueprintStructureRail
                    beats={blueprint.beats}
                    shotBeatMap={shotBeatMap}
                    hoveredBeat={hoveredBeat}
                    onHoverBeat={setHoveredBeat}
                  />
                )}
                <VideoShotInspector
                  shots={shots}
                  ratio={form.ratio}
                  selected={selectedShot}
                  onSelect={setSelectedShot}
                  onChangeShot={(i, next) => {
                    const arr = [...shots];
                    arr[i] = next;
                    writeShots(arr);
                  }}
                  onRemoveShot={(i) => writeShots(shots.filter((_, j) => j !== i))}
                  onInsertShot={insertShot}
                  onPreview={setLightboxIndex}
                  onRegenerateFrame={regenerateFrame}
                  onUploadFrame={uploadFrameForShot}
                  generatingSet={framingSet}
                  busy={framingBatch}
                  segments={compilePreview.segments}
                  charBudget={compilePreview.charBudget}
                  previewLoading={compilePreview.loading}
                  previewError={compilePreview.staleError}
                  refCatalog={refCatalog}
                  autoMotion={
                    selectedShot !== null && shots[selectedShot]
                      ? isAutoMotion(autoMotion, selectedShot, shots[selectedShot])
                      : false
                  }
                  factTexts={form.factTexts}
                  voiceSpeechRate={form.voiceSpeechRate}
                  /* 全片视图的插槽 —— 没选镜时右栏装「整条片」:结构 + 预览 + 积分 + 生成。
                     ★检视面板自己不去拿数据,由页面装配(它不该知道积分怎么算)。 */
                  overviewSlot={
                    <VideoCreateCockpit
                      form={form}
                      shots={shots}
                      autoMotion={autoMotion}
                      templateCoverUrl={tpl?.coverUrl}
                      effectiveDurationSec={effectiveDuration}
                    />
                  }
                />
                {/* ★出片条恒显,不随「在看哪一镜」出没(见 VideoSubmitBar 头注):
                    出片是这一页的终点,任何时候都得够得着。 */}
                <VideoSubmitBar
                  credits={submitCredits ?? 0}
                  onSubmit={onSubmit}
                  submitting={submitting}
                  busy={uploadingBgm || bootstrapping || framingBatch}
                  busyLabel={
                    bootstrapping
                      ? "加载海报参数中…"
                      : framingBatch
                        ? frameGenStatus === "COMPOSING"
                          ? "AI 编排中…"
                          : "台本图出图中…"
                        : undefined
                  }
                />
              </div>
            </div>

            {/* 大图看图层:portal 到 body,由页面持有 index 才翻得动跨镜 ←/→。 */}
            <ShotLightbox
              shots={shots}
              index={lightboxIndex}
              onClose={() => setLightboxIndex(null)}
              onIndexChange={setLightboxIndex}
            />
          </div>
        ) : isPurposeFlow ? (
          /* ══════════ 幕一 · 目的流(D 批两栏,设计稿 7d586023 V4)══════════
             治右侧大段空白:左栏专注输入,右栏「这条片」摘要 + 机器 + 报价 + CTA(sticky)。 */
          <div className="mt-4">
            <div
              data-testid="purpose-flow-two-col"
              className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start"
            >
              {/* 左栏:时长(独立行) → 拍法卡(换拍法+节拍带) → 想拍什么+素材 → 画面层(比例+风格) */}
              <div className="min-w-0 space-y-4">
                {durationCard}
                {shootPlanCard}
                {intakeDeck}
              </div>
              {/* 右栏(sticky):这条片摘要 + 合身度 + 精简机器 + 报价 + 主 CTA + 直发次链接 */}
              <div className="space-y-3 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
                {planSummaryPanel}
                {advancedDense}
                {purposeCta}
              </div>
            </div>
            {jobBar}
            {errorBar}
          </div>
        ) : isFreeFlow ? (
          /* ══════════ 幕一 · 自由创作(D 批独立精简页,设计稿 FreeCreate 板)══════════
             不穿目的壳(无横幅/拍法卡/目的 chips/编排费首屏);直发优先,编排是次链接。 */
          <div className="mt-4">
            <div
              data-testid="free-flow-two-col"
              className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start"
            >
              <div className="min-w-0 space-y-4">{freeCreateBody}</div>
              <div className="space-y-3 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
                {planSummaryPanel}
                {advancedDense}
                {freeCta}
              </div>
            </div>
            {jobBar}
            {errorBar}
          </div>
        ) : (
          /* ══════════ 幕一 · 蓝图 / 海报 / 模板(单列,现状不动)══════════ */
          <div className="mt-4 space-y-4">
            {/* A1:时长×拍法 联动卡(目的流才有,这里恒 null)。 */}
            {shootPlanCard}
            {/* B3:备料卡(蓝图态含①换产品 + ②介绍产品)在前,首屏让给操作;
                原片拆解折叠收在其后,查证信息不抢首屏。 */}
            {intakeDeck}
            {blueprintOriginalCut}
            {/* 海报流:一键成片按钮(它的输入在备料台③里,按钮跟着它走)。 */}
            {fromPoster && (
              <div className="flex justify-end">
                <Button
                  type="button"
                  size="lg"
                  className="h-10 gap-1.5"
                  disabled={composing || bootstrapping || framingBatch}
                  onClick={handleRefuse}
                >
                  <Wand2 className="h-4 w-4" />
                  {composing || framingBatch
                    ? frameGenStatus === "COMPOSING"
                      ? "AI 编排中…"
                      : "出图中…"
                    : form.storyMode
                      ? "一键生成故事视频"
                      : /* 0901 改口:原文案「让海报动起来」与海报详情页直连 I2V 弹层撞名 ——
                           同一句话在两条能力完全不同的路上各出现一次,用户分不清点的是哪条。
                           这边是忠于海报版式的**编排**成片,叫它编排的名字。 */
                        "忠于海报编成片"}
                </Button>
              </div>
            )}
            {jobBar}
            {errorBar}
          </div>
        )}
      </div>

      {/**
       * 「不编排直接出片」知情确认 —— 组件在 components/BareSendConfirmDialog(见那边头注)。
       *
       * ★2026-08-19 老板走查后**再次反转**按钮等级:确认位放的是**用户已经表达的意图**
       *   (直接出片),推荐动作退回次级。上一版(2026-08-06)把推荐动作放进 AlertDialogAction,
       *   老板点了右下角那颗实心按钮 → 走了编排。劝返靠文案,不靠抢确认位。
       * ★闸仍是这个弹窗本身:唯一能到达裸发的入口就是那颗次级触发按钮,它必经这里。
       */}
      <BareSendConfirmDialog
        open={bareSendConfirmOpen}
        onOpenChange={setBareSendConfirmOpen}
        bareSendCredits={quotedBareSend ?? 0}
        bareSendDurationSec={bareSendDurationSec(form, bareSendMaxSec(selectedCap?.durationRange))}
        clampedTier={
          bareSendClampsTier(form, bareSendMaxSec(selectedCap?.durationRange))
            ? (form.briefDuration ?? null)
            : null
        }
        tierTargetSec={briefTargetSec(form.briefDuration)}
        onConfirmBareSend={() => {
          setBareSendConfirmOpen(false);
          void onSubmit();
        }}
        onGoCompose={() => {
          setBareSendConfirmOpen(false);
          void handleCompose();
        }}
      />

      {/**
       * 「重新编排」知情确认(批⑫a)—— 组件在 components/RecomposeConfirmDialog(见那边头注)。
       *
       * ★闸不在这颗弹窗上,在 `guardedCompose`:三个编排入口(主按钮 / 摘要条小按钮 /
       *   模版填空面板)全部改调它,已有分镜就必经这里。把闸挂在某一个 onClick 上,
       *   另外两条路照样静默清空分镜。
       * ★关闭(点「先不」/ Esc / 点外面)必须把待执行的那一次编排丢掉 —— 留着它,
       *   下次开弹窗点确认跑的就是上一次那份 baseForm(模版路尤其致命:填的字都不是这次的)。
       * ★`canRestore` 按真实条件传,不写死 true:弹窗里「编排完可以切回来」是一句承诺,
       *   槽里没东西时说它就是当场撒谎。当前它恒真(闸只在有分镜时开,而有分镜就必然被
       *   captureStoryboardBackup 存下),但条件一变,弹窗跟着改口而不是替我们圆谎。
       */}
      <RecomposeConfirmDialog
        open={recomposeConfirmOpen}
        onOpenChange={(open) => {
          if (!open) pendingComposeRef.current = null;
          setRecomposeConfirmOpen(open);
        }}
        shotCount={form.panelScenes?.length ?? 0}
        canRestore={hasStoryboardShots(form)}
        onConfirm={() => {
          setRecomposeConfirmOpen(false);
          const run = pendingComposeRef.current;
          pendingComposeRef.current = null;
          void run?.();
        }}
      />

      {/* 尾句裁切提交明示(0817 拍板)—— 超语速上限的口播会被 ai 硬裁,提交前把裁点
          亮出来知情同意。推荐动作(回去改文案/加时长)是主按钮,照裁提交是次级。 */}
      <AlertDialog
        open={voCutNotices !== null}
        onOpenChange={(open) => {
          if (!open) setVoCutNotices(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>部分口播太长,超出的部分不会念出也不会出字幕</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>每镜能念的字数跟时长走,超出的部分生成时会被截掉(宁留白不念糊):</p>
                {(voCutNotices ?? []).map((n) => (
                  <div
                    key={n.shotIndex}
                    data-testid="video-vocut-shot"
                    className="rounded-md border border-amber-400/60 bg-amber-50/70 px-2.5 py-1.5 text-xs leading-relaxed text-amber-800 dark:border-amber-400/40 dark:bg-amber-950/30 dark:text-amber-300"
                  >
                    <p className="font-medium">{voiceoverCutLine(n)}</p>
                    <p className="mt-0.5">以下内容不会念出也不会出字幕:</p>
                    <VoiceoverDroppedList prediction={n.prediction} className="mt-0.5" />
                    <p className="mt-0.5">{voiceoverFixAdvice(n.prediction)}</p>
                  </div>
                ))}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              className="text-muted-foreground"
              data-testid="video-vocut-confirm"
              onClick={() => {
                setVoCutNotices(null);
                void submitCreateTask();
              }}
            >
              知道了,照此提交
            </Button>
            <AlertDialogAction onClick={() => setVoCutNotices(null)}>
              返回修改文案
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
